import { useState, useCallback, useEffect, useMemo, useRef, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import type {
  DashboardArchiveEntry,
  DashboardArchiveSetInput,
  DashboardFavoriteSetInput,
  DashboardPreferences,
  GameSummary,
  GameSummaryLite,
  ProjectCardAvailability,
  ProjectCardSummary,
  ProjectCardSummaryLite,
  LocalProjectCardSummary,
  LocalProjectCardSummaryLite,
  GameStatus,
  GameStatusBadge,
  PublishProgress,
  DiscoveredGame,
  ImportGamePreview,
  ImportGameProjectKind,
  ProjectRef,
  CloudAccountStatus,
} from '@volley/crucible-ipc-types/ipc-channels';
import { useIpcQuery } from '../hooks/useIpc';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import { useCardVisibility } from '../hooks/useCardVisibility';
import { useGameStatusLite } from '../hooks/useGameStatusLite';
import { useTemplateUpdateCheck } from '../hooks/useTemplateUpdateCheck';
import { useClientCapabilities } from '../transport/context';
import { canInvokeChannel } from '@volley/crucible-client';
import { LoadingOverlay } from '../components/LoadingOverlay';
import { OnboardingOverlay } from '../components/OnboardingOverlay';
import { GameStatusPanel, formatTimeAgo } from '../components/GameStatusPanel';
import { useDeployStatus } from '../hooks/useDeployStatus';
import { TemplateUpdateBadge } from '../components/TemplateUpdateBadge';
import { InterviewInProgressBadge } from '../components/InterviewInProgressBadge';
import { InstallStateChip } from '../components/InstallStateChip';
import { UpdateTemplateModal } from '../components/UpdateTemplateModal';
import { CrucibleErrorState } from '../components/CrucibleErrorState';
import { FoundryShellHeader } from '../components/FoundryShellHeader';
import { useAppFrame } from '../AppFrameContext';
import { unflattenIpcError, type SerialisedError } from '@volley/crucible-ipc-types/error-types';
import {
  isTransientCloudAuthError,
  TRANSIENT_CLOUD_AUTH_TITLE,
  TRANSIENT_CLOUD_AUTH_DETAIL,
} from './cloud-auth-error';
import { GenreFallback } from '../components/GenreFallback';
import { UpdateBanner } from '../components/UpdateBanner';
import {
  Settings as SettingsIcon,
  Download,
  Check,
  RefreshCw,
  AlertTriangle,
  Plus,
  Search,
  X,
  Archive,
  ExternalLink,
  RotateCcw,
  Sparkles,
  Star,
  Globe,
  Cloud,
  CloudOff,
  LogIn,
  Home,
  FolderInput,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { StatusPill } from '../components/ui/status-pill';
import {
  ProjectAccessDialog,
  projectAccessAfterCloudSignIn,
} from '../components/ProjectAccessDialog';
import { useLocalOperationalReadiness } from '../local-operational-readiness';
import { inferGenreKey } from '@volley/crucible-ipc-types/genre-fallback';
import {
  countHiddenTestGames,
  dashboardPreferenceKey,
  filterDashboardCommunityGames,
  filterDashboardLocalGames,
  isDashboardFavorited,
  resolveDashboardFavorites,
  type DashboardFavoriteCard,
  type LocalGamePreferenceSummary,
} from './dashboard-preferences';

function useGitHubUser() {
  return useIpcQuery('github:get-user', undefined, { staleTime: 5 * 60_000 });
}

function hasPlacementAwareAvailability(project: {
  placement?: 'local' | 'cloud';
  availability?: ProjectCardAvailability;
}): boolean {
  return project.placement !== undefined && project.availability !== undefined;
}

interface DashboardLocalPreferenceGame extends LocalGamePreferenceSummary {
  displayName: string;
  repoUrl?: string;
}

type DashboardViewId = 'mine' | 'favorites' | 'community' | 'archived';

export function projectDeleteCopy(input: {
  placement: 'local' | 'cloud';
  name: string;
  imported?: boolean;
}): { prompt: string; label: string; ariaLabel: string } {
  if (input.placement === 'cloud') {
    return {
      prompt: `Delete "${input.name}" from Foundry Cloud? This removes the Cloud project and cannot be undone.`,
      label: 'Delete from Foundry Cloud',
      ariaLabel: `Delete ${input.name} from Foundry Cloud`,
    };
  }
  if (input.imported) {
    return {
      prompt: `Remove "${input.name}" from Foundry? This keeps the project files on disk.`,
      label: 'Remove from Foundry',
      ariaLabel: `Remove ${input.name} from Foundry`,
    };
  }
  return {
    prompt: `Delete "${input.name}" locally? This removes the local files but keeps the GitHub repo.`,
    label: 'Delete locally',
    ariaLabel: `Delete ${input.name} locally`,
  };
}

/**
 * The confirm copy for archiving a project from its card.
 *
 * Shared by every surface that can archive a project card — the lazy
 * card, the legacy card, and (since #3527) the Favorites card — so the
 * three prompts can't drift apart on what archiving actually does to
 * the files on disk.
 */
export function projectArchiveConfirmPrompt(name: string): string {
  return `Archive "${name}"? It will be hidden from the Dashboard until you restore it. Files stay on disk.`;
}

/**
 * Invalidate every query key that backs a Dashboard game list.
 *
 * The Dashboard renders ONE of two list queries at a time
 * (`game:list` on the legacy path, `game:list:lite` on the lazy path —
 * see `useFeatureFlags().lazyDashboardLoad`). Mutations that change
 * the list contents (onboarding completion, game create / delete,
 * hero generation, community-game clone) need to refresh whichever
 * path is active. React Query's partial matching is index-by-index:
 * a single invalidate on `['game:list']` does NOT match
 * `['game:list:lite']` (the strings differ at index 0). Without
 * touching BOTH keys the lazy-path user sees stale data until they
 * navigate away and back.
 *
 * Folded post-merge on PR #706 — the bots called this out as
 * `Macroscope HIGH` / `Cursor MEDIUM` on the merged code, but the
 * inline review wasn't iterated before merge. See follow-up PR /
 * `feedback_github_bot_iteration.md` in the agent memory.
 */
export function invalidateGameLists(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: ['game:list'] });
  void qc.invalidateQueries({ queryKey: ['game:list:lite'] });
  void qc.invalidateQueries({ queryKey: ['game:list:local-lite'] });
  void qc.invalidateQueries({ queryKey: ['game:list:cloud-lite'] });
}

function invalidateDashboardPreferences(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: ['dashboard:preferences:get'] });
}

function repoUrlFromFullName(repoFullName: string | undefined): string | undefined {
  return repoFullName ? `https://github.com/${repoFullName}` : undefined;
}

function openExternalUrl(url: string | undefined): void {
  if (!url) return;
  window.open(url, '_blank', 'noopener,noreferrer');
}

function localLiteToPreferenceGame(game: ProjectCardSummaryLite): DashboardLocalPreferenceGame {
  return {
    gameId: game.gameId,
    displayName: game.displayName,
    category: game.category,
    heroImage: game.heroImage,
    description: game.description,
    clonedFrom: game.clonedFrom,
    placement: game.placement,
    availability: game.availability,
    disabledReason: game.disabledReason,
    repoUrl: repoUrlFromFullName(game.clonedFrom),
  };
}

function localSummaryToPreferenceGame(game: ProjectCardSummary): DashboardLocalPreferenceGame {
  return {
    gameId: game.gameId,
    displayName: game.name,
    category: game.category,
    heroImage: game.heroImage,
    description: game.description,
    clonedFrom: game.clonedFrom,
    placement: game.placement,
    availability: game.availability,
    disabledReason: game.disabledReason,
    repoUrl: repoUrlFromFullName(game.clonedFrom),
  };
}

function localLitePreferencePayload(
  game: ProjectCardSummaryLite,
  scope: 'local' | 'cloud' = game.placement === 'cloud' ? 'cloud' : 'local',
): Omit<DashboardFavoriteSetInput, 'favorite'> & Omit<DashboardArchiveSetInput, 'archived'> {
  return {
    scope,
    gameId: game.gameId,
    project: { placement: scope, gameId: game.gameId },
    displayName: game.displayName,
    repoFullName: game.clonedFrom,
    repoUrl: repoUrlFromFullName(game.clonedFrom),
    heroImage: game.heroImage,
    description: game.description,
  };
}

function localSummaryPreferencePayload(
  game: ProjectCardSummary,
  scope: 'local' | 'cloud' = game.placement === 'cloud' ? 'cloud' : 'local',
): Omit<DashboardFavoriteSetInput, 'favorite'> & Omit<DashboardArchiveSetInput, 'archived'> {
  return {
    scope,
    gameId: game.gameId,
    project: { placement: scope, gameId: game.gameId },
    displayName: game.name,
    repoFullName: game.clonedFrom,
    repoUrl: repoUrlFromFullName(game.clonedFrom),
    heroImage: game.heroImage,
    description: game.description,
  };
}

function communityPreferencePayload(
  game: DiscoveredGame,
): Omit<DashboardFavoriteSetInput, 'favorite'> & Omit<DashboardArchiveSetInput, 'archived'> {
  return {
    scope: 'community',
    gameId: game.gameId,
    displayName: game.displayName,
    repoFullName: game.repoFullName,
    repoUrl: game.repoUrl,
    heroImage: game.heroImage,
    description: game.description,
  };
}

/**
 * The open-in-editor glyph carried by every project card.
 *
 * Extracted when the Favorites card gained the same affordance (#3527):
 * the icon is how a user recognises the action across the Dashboard, so
 * the three call sites must draw the identical mark, not three copies
 * that can drift.
 */
function CursorEditorGlyph({ size = 14 }: Readonly<{ size?: number }>): React.JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M5 2L2 8l3 6M11 2l3 6-3 6"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M8 6v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

/**
 * Preference-mutation payload for a card in the Favorites section.
 *
 * The card metadata matters on the ARCHIVE path (#3527): the Archived
 * section labels its rows `displayName ?? repoFullName ?? gameId`, so
 * archiving from Favorites with an identity-only payload would drop the
 * game into the Archived list showing a raw game id. Same shape as
 * `localSummaryPreferencePayload` / `communityPreferencePayload`, built
 * from the already-resolved favorite card.
 */
function favoritePreferencePayload(
  favorite: DashboardFavoriteCard,
): Omit<DashboardFavoriteSetInput, 'favorite'> & Omit<DashboardArchiveSetInput, 'archived'> {
  return {
    scope: favorite.scope,
    gameId: favorite.gameId,
    // `project` is required for Local/Cloud cards and must be omitted for
    // community catalogue rows, which have no placement-qualified project.
    ...(favorite.scope === 'local' || favorite.scope === 'cloud'
      ? { project: { placement: favorite.scope, gameId: favorite.gameId } }
      : {}),
    displayName: favorite.displayName,
    repoFullName: favorite.repoFullName,
    repoUrl: favorite.repoUrl,
    heroImage: favorite.heroImage,
    description: favorite.description,
  };
}

/**
 * Issue #441 — orphan-repo cleanup card.
 *
 * Rendered separately from <CrucibleErrorState> because the warning
 * is a SECOND, non-fatal issue surfaced alongside the primary
 * create failure (the scaffold-rollback couldn't delete the empty
 * remote repo, so the user has an orphan on GitHub).
 *
 * Codex YELLOW #1 fold (PowerShell 5.1 portability): when the
 * `recovery` snippet contains multiple commands, the core emits
 * them on separate lines (joined by `\n`) because PS 5.1 doesn't
 * support `&&` chaining. The card renders each line as its own
 * `<code>` block so the user can copy each command individually —
 * a `select-all` on one block selects only that line. Single-line
 * recoveries fall through to the same rendering with a single
 * block, so the no-newline case still works exactly as before.
 *
 * Extracted as a named export so the SSR-only Dashboard test can
 * pin the two-block rendering contract without spinning up the
 * full Dashboard page (which depends on react-query, react-router,
 * IPC stubs, etc.).
 */
export function RollbackWarningCard({
  message,
  recovery,
}: {
  message: string;
  recovery?: string;
}): React.JSX.Element {
  const lines = recovery ? recovery.split('\n').filter((l) => l.length > 0) : [];
  return (
    <div
      data-testid="rollback-warning-card"
      className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3"
    >
      <p className="text-sm text-amber-100">{message}</p>
      {lines.length > 0 && (
        <>
          <p className="mt-2 text-xs text-amber-100/70">
            {lines.length > 1
              ? "Run these two commands in order in a terminal (PowerShell 5.1 doesn't support `&&`; the second only needs to run if the first succeeded):"
              : 'Run this in a terminal to clean up the orphan repo:'}
          </p>
          {lines.map((line, i) => (
            <code
              key={i}
              data-testid="rollback-warning-recovery-line"
              className="mt-1 block select-all break-all rounded bg-black/40 px-3 py-2 font-mono text-xs text-amber-50"
            >
              {line}
            </code>
          ))}
        </>
      )}
    </div>
  );
}

function DashboardSearchBar({
  value,
  onChange,
  presentation = 'toolbar',
}: {
  value: string;
  onChange: (v: string) => void;
  presentation?: 'toolbar' | 'rail';
}): React.JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <div className="relative flex items-center">
      <Search
        size={13}
        className="pointer-events-none absolute left-2.5"
        style={{ color: 'var(--text-3)' }}
      />
      <Input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Search games..."
        className={`${presentation === 'rail' ? 'h-10 w-full rounded-lg' : 'w-44 rounded-full py-1.5'} border border-white/[0.10] bg-white/[0.05] pl-8 pr-7 text-[13px] placeholder-white/25 outline-none transition-colors focus:border-white/[0.20] focus:bg-white/[0.08]`}
        style={{ color: 'var(--text-2)' }}
      />
      {value && (
        <Button
          variant="ghost"
          size="icon"
          className="absolute right-2 h-6 w-6 text-white/30 hover:text-white/60"
          onClick={() => {
            onChange('');
            inputRef.current?.focus();
          }}
          aria-label="Clear search"
        >
          <X size={13} />
        </Button>
      )}
    </div>
  );
}

function DashboardWorkbenchRail({
  active,
  counts,
  showCommunity,
  canImport,
  searchQuery,
  hiddenCount,
  showTestGames,
  onSearchChange,
  onSelect,
  onCreate,
  onImport,
  footer,
}: {
  active: DashboardViewId;
  counts: {
    mine: number;
    favorites: number;
    community?: number;
    archived: number;
  };
  showCommunity: boolean;
  canImport: boolean;
  searchQuery: string;
  hiddenCount: number;
  showTestGames: boolean;
  onSearchChange: (value: string) => void;
  onSelect: (view: DashboardViewId) => void;
  onCreate: () => void;
  onImport: () => void;
  footer?: ReactNode;
}): React.JSX.Element {
  const views: Array<{
    id: DashboardViewId;
    label: string;
    Icon: typeof Home;
    count?: number;
  }> = [
    { id: 'mine', label: 'My Games', Icon: Home, count: counts.mine },
    { id: 'favorites', label: 'Favorites', Icon: Star, count: counts.favorites },
    ...(showCommunity
      ? [{ id: 'community' as const, label: 'Community', Icon: Globe, count: counts.community }]
      : []),
    { id: 'archived', label: 'Archived', Icon: Archive, count: counts.archived },
  ];

  return (
    <aside
      className="app-region-no-drag flex w-[252px] shrink-0 flex-col border-r border-white/[0.08] px-5 pb-5 pt-6"
      aria-label="Dashboard workbench"
      data-testid="dashboard-workbench-rail"
    >
      <div className="space-y-3">
        <Button
          className="h-11 w-full justify-center rounded-xl text-[14px]"
          onClick={onCreate}
          data-testid="dashboard-new-game-button"
        >
          <Plus size={16} strokeWidth={2} />
          New game
        </Button>
        {canImport && (
          <Button
            variant="secondary"
            className="h-11 w-full justify-center rounded-xl border border-white/[0.14] bg-transparent text-[14px]"
            onClick={onImport}
            data-testid="dashboard-import-project-button"
          >
            <FolderInput size={16} strokeWidth={1.7} />
            Import project
          </Button>
        )}
      </div>

      <div className="mt-8">
        <DashboardSearchBar value={searchQuery} onChange={onSearchChange} presentation="rail" />
        {hiddenCount > 0 && !showTestGames && (
          <p
            className="mt-2 px-1 text-[11px]"
            style={{ color: 'var(--text-3)' }}
            title="Toggle in Settings → Preferences → Show test games"
          >
            {hiddenCount} test hidden
          </p>
        )}
      </div>

      <nav className="mt-5" aria-label="Project views">
        <div role="tablist" aria-label="Dashboard sections" className="space-y-1">
          {views.map(({ id, label, Icon, count }) => {
            const selected = active === id;
            return (
              <button
                key={id}
                id={`dashboard-view-${id}`}
                type="button"
                role="tab"
                aria-selected={selected}
                aria-controls="dashboard-project-view"
                onClick={() => onSelect(id)}
                className={`relative flex h-11 w-full items-center gap-3 rounded-lg px-3 text-left text-[13px] transition-colors ${
                  selected ? 'bg-white/[0.065]' : 'hover:bg-white/[0.035]'
                }`}
                style={{ color: selected ? 'var(--color-brand)' : 'var(--text-2)' }}
              >
                {selected && (
                  <span className="absolute -left-5 h-7 w-0.5 rounded-r bg-[var(--color-brand)]" />
                )}
                <Icon
                  size={16}
                  strokeWidth={1.7}
                  fill={id === 'favorites' && selected ? 'currentColor' : 'none'}
                />
                <span className="min-w-0 flex-1 truncate">{label}</span>
                {count !== undefined && (
                  <span
                    className="text-[12px] tabular-nums"
                    style={{ color: selected ? 'var(--color-brand)' : 'var(--text-3)' }}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </nav>

      {footer && (
        <div
          className="mt-auto border-t border-white/[0.08] pt-4"
          data-testid="dashboard-workbench-footer"
        >
          {footer}
        </div>
      )}
    </aside>
  );
}

/**
 * Renders the Dashboard's Cloud account status and recovery action.
 *
 * Args:
 *   props: Current query state and the action used to sign in or retry.
 *
 * Returns:
 *   The status pill or account action button appropriate for the current state.
 *
 * Raises:
 *   None.
 */
function DashboardCloudAccountControl({
  status,
  pending,
  statusError,
  actionError,
  onAction,
}: {
  status: CloudAccountStatus | undefined;
  pending: boolean;
  statusError: string | null;
  actionError: string | null;
  onAction: () => void;
}): React.JSX.Element {
  // Only an active request should use the disabled loading treatment.
  if (pending) {
    return (
      <Button variant="secondary" size="sm" disabled aria-label="Checking Cloud account status">
        <RefreshCw size={14} className="animate-spin" />
        Checking Cloud…
      </Button>
    );
  }

  // A failed initial query needs an actionable state because no status data exists yet.
  if (status === undefined && statusError) {
    return (
      <Button
        variant="secondary"
        size="sm"
        onClick={onAction}
        aria-label="Retry Cloud account status"
        title={statusError}
      >
        <AlertTriangle size={14} />
        Cloud status unavailable
      </Button>
    );
  }

  // The first request can briefly be idle before React Query starts fetching.
  if (status === undefined) {
    return (
      <Button variant="secondary" size="sm" disabled aria-label="Checking Cloud account status">
        <RefreshCw size={14} className="animate-spin" />
        Checking Cloud…
      </Button>
    );
  }

  // Successful eligibility checks are informational and need no action.
  if (status.state === 'eligible') {
    return (
      <StatusPill
        intent="success"
        dot={false}
        className="h-8 gap-1.5 px-3"
        title="Cloud projects are connected and available on this Mac."
      >
        <Cloud size={14} />
        Cloud connected
      </StatusPill>
    );
  }

  // Ineligible accounts are signed in but cannot activate Cloud projects.
  if (status.state === 'ineligible') {
    return (
      <StatusPill
        intent="warning"
        dot={false}
        className="h-8 gap-1.5 px-3"
        title="This signed-in account is not in the Foundry Cloud cohort."
      >
        <CloudOff size={14} />
        Cloud access unavailable
      </StatusPill>
    );
  }

  // Signed-out and unavailable states share the sign-in/retry action surface.
  const signedOut = status.state === 'signed-out';
  const label = actionError
    ? signedOut
      ? 'Cloud sign-in failed'
      : 'Cloud connection failed'
    : signedOut
      ? 'Cloud: Sign in'
      : 'Cloud unavailable';
  return (
    <Button
      variant="secondary"
      size="sm"
      onClick={onAction}
      aria-label={signedOut ? 'Sign in to Cloud' : 'Retry Cloud connection'}
      title={
        actionError ??
        (signedOut
          ? 'Sign in to see and create Cloud projects.'
          : `Cloud account status is unavailable (${status.reason}).`)
      }
    >
      {actionError ? (
        <AlertTriangle size={14} />
      ) : signedOut ? (
        <LogIn size={14} />
      ) : (
        <RefreshCw size={14} />
      )}
      {label}
    </Button>
  );
}

export function Dashboard(): React.JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  useEffect(
    () =>
      window.crucible.on('cloud-projects:invalidated', () => {
        queryClient.removeQueries({ queryKey: ['game:list'] });
        queryClient.removeQueries({ queryKey: ['game:list:lite'] });
        queryClient.removeQueries({ queryKey: ['game:list:cloud-lite'] });
        invalidateGameLists(queryClient);
      }),
    [queryClient],
  );
  const capabilities = useClientCapabilities();
  const frame = useAppFrame();
  const showCommunityGames = capabilities.hasLocalFilesystem;
  // Dashboard lazy-load path (`docs/dashboard-lazy-load-plan.md`):
  // we now switch between two list queries based on the
  // `lazyDashboardLoad` flag. BOTH queries are gated on
  // `flags.loaded` so the renderer doesn't briefly fire the wrong
  // IPC during the first ~ms of mount (plan line 238).
  const flags = useFeatureFlags();
  const fullscreenFrame = frame === 'fullscreen';
  const { data: ghUser } = useGitHubUser();
  const lazyPath = flags.lazyDashboardLoad;
  const showCloudAccountControl =
    flags.loaded &&
    flags.cloudProjectPlacement &&
    fullscreenFrame &&
    capabilities.hasLocalFilesystem;
  const cloudAccountQuery = useIpcQuery('cloud-account:get-status', undefined, {
    enabled: showCloudAccountControl,
    staleTime: 30_000,
  });
  const [cloudAccountActionPending, setCloudAccountActionPending] = useState(false);
  const [cloudAccountActionError, setCloudAccountActionError] = useState<string | null>(null);
  const splitDesktopLite = lazyPath && capabilities.hasLocalFilesystem;
  const liteQuery = useIpcQuery('game:list:lite', undefined, {
    enabled: flags.loaded && lazyPath && !splitDesktopLite,
  });
  const localLiteQuery = useIpcQuery('game:list:local-lite', undefined, {
    enabled: flags.loaded && splitDesktopLite,
  });
  const localLiteData = localLiteQuery.data ?? [];
  const cloudLiteQuery = useIpcQuery('game:list:cloud-lite', localLiteData, {
    enabled:
      flags.loaded &&
      splitDesktopLite &&
      flags.cloudProjectPlacement &&
      localLiteQuery.data !== undefined,
  });
  const legacyQuery = useIpcQuery('game:list', undefined, { enabled: flags.loaded && !lazyPath });
  const preferencesQuery = useIpcQuery('dashboard:preferences:get', undefined);
  // Whichever path is active produces a normalised list view-model
  // the rest of the Dashboard renders. The lite path uses
  // `<LazyGameCard>` (per-card hydrate); the legacy path keeps the
  // existing `<GameCard>` shape — emergency rollback discipline.
  const semanticPlacement = capabilities.defaultProjectPlacement ?? 'local';
  const liteSourceData = splitDesktopLite
    ? [...localLiteData, ...(cloudLiteQuery.data ?? [])]
    : (liteQuery.data ?? []);
  const lazyData: ProjectCardSummaryLite[] = lazyPath
    ? liteSourceData.map((project) => {
        const game = project as unknown as GameSummaryLite & {
          availability?: ProjectCardAvailability;
        };
        const placement = game.placement ?? semanticPlacement;
        return hasPlacementAwareAvailability(game)
          ? (project as ProjectCardSummaryLite)
          : placement === 'cloud'
            ? ({
                ...game,
                placement: 'cloud',
                availability: 'available',
                localPath: undefined,
              } as ProjectCardSummaryLite)
            : ({
                ...game,
                placement: 'local',
                availability: 'available',
                localPath: game.localPath ?? '',
              } as ProjectCardSummaryLite);
      })
    : [];
  const legacyData: ProjectCardSummary[] = !lazyPath
    ? (legacyQuery.data ?? []).map((project) => {
        const game = project as unknown as GameSummary & {
          availability?: ProjectCardAvailability;
        };
        const placement = game.placement ?? semanticPlacement;
        return hasPlacementAwareAvailability(game)
          ? (project as ProjectCardSummary)
          : placement === 'cloud'
            ? ({
                ...game,
                placement: 'cloud',
                availability: 'available',
                localPath: undefined,
              } as ProjectCardSummary)
            : ({ ...game, placement: 'local', availability: 'available' } as ProjectCardSummary);
      })
    : [];
  const loading = lazyPath
    ? !flags.loaded ||
      (splitDesktopLite ? localLiteQuery.isLoading : liteQuery.isLoading) ||
      preferencesQuery.isLoading
    : !flags.loaded || legacyQuery.isLoading || preferencesQuery.isLoading;
  // The split catalogue is one dashboard load, so either half must surface its failure.
  const error =
    preferencesQuery.error ??
    (lazyPath
      ? splitDesktopLite
        ? (localLiteQuery.error ?? cloudLiteQuery.error)
        : liteQuery.error
      : legacyQuery.error);
  // #3123 — a truthful Retry for the game-list load. Refetch whichever
  // list query is active (that's where a cloud `game.list@1`
  // auth_failure surfaces); also refetch preferences so a Retry recovers
  // the whole error surface, not just half of it.
  const refetchGames = useCallback((): void => {
    if (lazyPath && splitDesktopLite) {
      void localLiteQuery.refetch();
      if (flags.cloudProjectPlacement && localLiteQuery.data !== undefined) {
        void cloudLiteQuery.refetch();
      }
    } else {
      void (lazyPath ? liteQuery.refetch() : legacyQuery.refetch());
    }
    void preferencesQuery.refetch();
  }, [
    cloudLiteQuery,
    flags.cloudProjectPlacement,
    lazyPath,
    liteQuery,
    localLiteQuery,
    legacyQuery,
    preferencesQuery,
    splitDesktopLite,
  ]);
  const localReadiness = useLocalOperationalReadiness(capabilities.hasLocalFilesystem);
  // Only runtimes that can run the check may present its setup failures.
  const localSetupNeeded = capabilities.hasLocalFilesystem && localReadiness.state === 'degraded';
  const [blockedProject, setBlockedProject] = useState<{
    project: ProjectRef;
    availability: ProjectCardAvailability;
    reason?: string;
  } | null>(null);
  const openProject = useCallback(
    (
      game: Pick<ProjectCardSummary, 'placement' | 'gameId' | 'availability' | 'disabledReason'>,
    ) => {
      if (game.placement === 'local' || game.availability === 'available') {
        void navigate(`/workspace/${game.placement}/${game.gameId}`);
        return;
      }
      setBlockedProject({
        project: { placement: game.placement, gameId: game.gameId },
        availability: game.availability,
        ...(game.disabledReason ? { reason: game.disabledReason } : {}),
      });
    },
    [navigate],
  );
  const showTransientCloudAuthError = error != null && isTransientCloudAuthError(error);
  const preferences = preferencesQuery.data;
  // CommunityGames deferral: `game:discover` competes with first-card
  // hydration for network. Only kick it off once the local list has
  // landed AND we're past first-paint idle (plan lines 199-201).
  // `idleReady` flips true on `requestIdleCallback` (or a 100ms
  // setTimeout fallback for Electron renderers that don't expose it)
  // after `listResolved` becomes true.
  const listResolved = lazyPath
    ? splitDesktopLite
      ? localLiteQuery.data !== undefined
      : liteQuery.data !== undefined
    : legacyQuery.data !== undefined;
  const [idleReady, setIdleReady] = useState(false);
  useEffect(() => {
    if (!listResolved || idleReady) return;
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    if (typeof w.requestIdleCallback === 'function') {
      const handle = w.requestIdleCallback(() => setIdleReady(true));
      return () => w.cancelIdleCallback?.(handle);
    }
    const timer = setTimeout(() => setIdleReady(true), 100);
    return () => clearTimeout(timer);
  }, [listResolved, idleReady]);
  const { data: onboardingDone } = useIpcQuery('app:onboarding-complete', undefined);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [activeView, setActiveView] = useState<DashboardViewId>('mine');

  useEffect(() => {
    if (!showCommunityGames && activeView === 'community') setActiveView('mine');
  }, [activeView, showCommunityGames]);

  const showingArchived = flags.newUi ? activeView === 'archived' : showArchived;
  const showingMyGames = flags.newUi ? activeView === 'mine' : !showArchived;
  const showingFavorites = flags.newUi && activeView === 'favorites';
  const showingCommunity = flags.newUi && activeView === 'community';
  const [searchQuery, setSearchQuery] = useState('');
  const { data: config } = useIpcQuery('config:get', undefined);
  const [editorError, setEditorError] = useState<string | null>(null);
  const editorErrorTimerRef = useRef<NodeJS.Timeout>(undefined);

  const showTestGames = config?.showTestGames ?? false;

  // Normalise both code paths to share archive/favorite filtering and
  // the community duplicate-suppression local-id list.
  const localPreferenceGames = useMemo(
    () =>
      lazyPath
        ? lazyData.map(localLiteToPreferenceGame)
        : legacyData.map(localSummaryToPreferenceGame),
    [lazyPath, lazyData, legacyData],
  );
  const unarchivedLazy = useMemo(
    () =>
      filterDashboardLocalGames(lazyData, preferences, {
        showTestGames: true,
        searchQuery: '',
        getDisplayName: (g) => g.displayName,
      }),
    [lazyData, preferences],
  );
  const unarchivedLegacy = useMemo(
    () =>
      filterDashboardLocalGames(legacyData, preferences, {
        showTestGames: true,
        searchQuery: '',
        getDisplayName: (g) => g.name,
      }),
    [legacyData, preferences],
  );
  const filteredLazy = useMemo(
    () =>
      filterDashboardLocalGames(lazyData, preferences, {
        showTestGames,
        searchQuery: '',
        getDisplayName: (g) => g.displayName,
      }),
    [lazyData, preferences, showTestGames],
  );
  const filteredLegacy = useMemo(
    () =>
      filterDashboardLocalGames(legacyData, preferences, {
        showTestGames,
        searchQuery: '',
        getDisplayName: (g) => g.name,
      }),
    [legacyData, preferences, showTestGames],
  );
  const searchLazy = useMemo(
    () =>
      filterDashboardLocalGames(lazyData, preferences, {
        showTestGames,
        searchQuery,
        getDisplayName: (g) => g.displayName,
      }),
    [lazyData, preferences, showTestGames, searchQuery],
  );
  const searchLegacy = useMemo(
    () =>
      filterDashboardLocalGames(legacyData, preferences, {
        showTestGames,
        searchQuery,
        getDisplayName: (g) => g.name,
      }),
    [legacyData, preferences, showTestGames, searchQuery],
  );

  const totalCount = lazyPath ? unarchivedLazy.length : unarchivedLegacy.length;
  const filteredCount = lazyPath ? filteredLazy.length : filteredLegacy.length;
  const searchCount = lazyPath ? searchLazy.length : searchLegacy.length;
  const hiddenCount = showTestGames ? 0 : countHiddenTestGames(localPreferenceGames, preferences);
  const localGameIds = lazyPath ? lazyData.map((g) => g.gameId) : legacyData.map((g) => g.gameId);
  const communityEnabled =
    showCommunityGames &&
    showingCommunity &&
    flags.loaded &&
    listResolved &&
    idleReady &&
    error == null;
  const communityWaitingForDiscovery =
    showCommunityGames && showingCommunity && !communityEnabled && error == null;
  const communityQuery = useIpcQuery('game:discover', undefined, { enabled: communityEnabled });
  const favoriteCards = useMemo(
    () =>
      resolveDashboardFavorites(preferences, {
        localGames: localPreferenceGames,
        communityGames: showCommunityGames ? (communityQuery.data ?? []) : [],
        getLocalDisplayName: (game) => game.displayName,
        showTestGames,
      }),
    [preferences, localPreferenceGames, communityQuery.data, showCommunityGames, showTestGames],
  );
  const searchedFavoriteCards = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return favoriteCards;
    return favoriteCards.filter((favorite) => favorite.displayName.toLowerCase().includes(query));
  }, [favoriteCards, searchQuery]);
  const communityCount = useMemo(() => {
    if (!communityQuery.data) return undefined;
    return filterDashboardCommunityGames(communityQuery.data, preferences, {
      localGameIds,
      showTestGames,
      searchQuery: '',
    }).length;
  }, [communityQuery.data, localGameIds, preferences, showTestGames]);
  const archivedCount = preferences?.archived.length ?? 0;

  const setArchive = useCallback(
    async (input: DashboardArchiveSetInput): Promise<void> => {
      await window.crucible.invoke('game:archive:set', input);
      invalidateDashboardPreferences(queryClient);
      void queryClient.invalidateQueries({ queryKey: ['game:discover'] });
    },
    [queryClient],
  );

  const setFavorite = useCallback(
    async (input: DashboardFavoriteSetInput): Promise<void> => {
      await window.crucible.invoke('game:favorite:set', input);
      invalidateDashboardPreferences(queryClient);
    },
    [queryClient],
  );

  const handleCloudAccountAction = useCallback((): void => {
    if (cloudAccountActionPending) return;
    setCloudAccountActionPending(true);
    setCloudAccountActionError(null);
    void (async () => {
      try {
        const current = cloudAccountQuery.data;
        const status =
          current?.state === 'signed-out'
            ? await window.crucible.invoke('cloud-account:sign-in')
            : (await cloudAccountQuery.refetch()).data;
        if (status) {
          queryClient.setQueryData(['cloud-account:get-status'], status);
        }
        if (status?.state === 'eligible') {
          await queryClient.invalidateQueries({ queryKey: ['game:list:cloud-lite'] });
          if (!splitDesktopLite) invalidateGameLists(queryClient);
        }
      } catch (error) {
        setCloudAccountActionError(error instanceof Error ? error.message : String(error));
      } finally {
        setCloudAccountActionPending(false);
      }
    })();
  }, [cloudAccountActionPending, cloudAccountQuery, queryClient, splitDesktopLite]);

  // Show onboarding on first launch
  useEffect(() => {
    if (onboardingDone === false) setShowOnboarding(true);
  }, [onboardingDone]);

  // Subscribe to editor errors
  useEffect(() => {
    const unsub = window.crucible.on('editor:error', (msg) => {
      setEditorError(msg);
      clearTimeout(editorErrorTimerRef.current);
      editorErrorTimerRef.current = setTimeout(() => setEditorError(null), 5000);
    });
    return () => {
      unsub();
      clearTimeout(editorErrorTimerRef.current);
    };
  }, []);

  const canImportLocalProject =
    flags.newUi &&
    capabilities.hasLocalFilesystem &&
    canInvokeChannel(capabilities, 'game:import:pick-directory');
  const [importOpen, setImportOpen] = useState(false);
  const handleImportClose = useCallback(() => setImportOpen(false), []);
  const handleImported = useCallback(
    (importedGameId: string) => {
      setImportOpen(false);
      invalidateGameLists(queryClient);
      void navigate(`/workspace/local/${importedGameId}`);
    },
    [navigate, queryClient],
  );
  const handleUseImportAsContext = useCallback(() => {
    setImportOpen(false);
    void navigate('/new-game');
  }, [navigate]);

  const dashboardActions = flags.newUi ? (
    <div className="flex items-center gap-2">
      <div
        className="flex items-center gap-1 rounded-full px-1 py-1"
        style={{ background: 'var(--bg-elev-1)' }}
      >
        <Button
          variant="secondary"
          size="icon"
          onClick={() => navigate('/settings')}
          style={{
            background: 'var(--color-crucible-bg)',
            color: 'var(--text-3)',
            transitionTimingFunction: 'var(--ease-flora)',
            transitionDuration: 'var(--motion-fast)',
          }}
          aria-label="Settings"
        >
          <SettingsIcon size={16} strokeWidth={1.75} />
        </Button>
        {ghUser && (
          <img
            src={ghUser.avatarUrl}
            alt={ghUser.login}
            title={ghUser.login}
            className="h-8 w-8 rounded-full object-cover ring-1 ring-white/10"
          />
        )}
      </div>
    </div>
  ) : (
    <div className="flex gap-3">
      <Button variant="secondary" size="sm" onClick={() => navigate('/devices')}>
        Devices
      </Button>
      <Button variant="secondary" size="sm" onClick={() => navigate('/settings')}>
        Settings
      </Button>
      <Button size="sm" onClick={() => navigate('/new-game')}>
        + New Game
      </Button>
    </div>
  );

  const workbenchFooter =
    showCloudAccountControl || !fullscreenFrame ? (
      <div className="space-y-3">
        {showCloudAccountControl && (
          <DashboardCloudAccountControl
            status={cloudAccountQuery.data}
            pending={cloudAccountQuery.isFetching || cloudAccountActionPending}
            statusError={cloudAccountQuery.error?.message ?? null}
            actionError={cloudAccountActionError}
            onAction={handleCloudAccountAction}
          />
        )}
        {!fullscreenFrame && dashboardActions}
      </div>
    ) : undefined;
  const activeViewTitle =
    activeView === 'mine'
      ? 'My games'
      : activeView === 'favorites'
        ? 'Favorites'
        : activeView === 'community'
          ? 'Community'
          : 'Archived';
  const activeViewCount =
    activeView === 'mine'
      ? totalCount
      : activeView === 'favorites'
        ? favoriteCards.length
        : activeView === 'community'
          ? communityCount
          : archivedCount;

  return (
    <div className="flex h-full flex-col">
      {/* Editor error toast */}
      {editorError && (
        <div className="fixed right-4 top-4 z-40 max-w-sm rounded-lg border border-red-500/30 bg-red-900/80 px-4 py-3 text-sm text-red-200 shadow-lg backdrop-blur-sm">
          {editorError}
        </div>
      )}

      {fullscreenFrame && (
        <FoundryShellHeader
          chrome="electron"
          showBrandMark={flags.newUi}
          actions={dashboardActions}
        />
      )}
      {flags.newUi && <UpdateBanner />}

      {/* Onboarding overlay (first launch) */}
      {showOnboarding && (
        <OnboardingOverlay
          onComplete={() => {
            setShowOnboarding(false);
            void window.crucible
              .invoke('app:set-onboarding-complete')
              .catch((err) => console.warn('[app:set-onboarding-complete] failed:', err));
            invalidateGameLists(queryClient);
          }}
        />
      )}

      <ProjectAccessDialog
        project={blockedProject?.project ?? null}
        availability={blockedProject?.availability ?? 'offline'}
        reason={blockedProject?.reason}
        onClose={() => setBlockedProject(null)}
        onSignIn={() => {
          const pending = blockedProject;
          if (!pending) return;
          void window.crucible.invoke('cloud-account:sign-in').then((status) => {
            invalidateGameLists(queryClient);
            if (status.state === 'eligible') {
              setBlockedProject(null);
              void navigate(`/workspace/cloud/${pending.project.gameId}`);
              return;
            }
            setBlockedProject((current) =>
              current?.project.gameId === pending.project.gameId
                ? {
                    ...current,
                    ...projectAccessAfterCloudSignIn(status),
                  }
                : current,
            );
          });
        }}
        onRetry={() => {
          refetchGames();
          setBlockedProject(null);
        }}
      />

      <div className="flex min-h-0 flex-1">
        {flags.newUi && (
          <DashboardWorkbenchRail
            active={activeView}
            counts={{
              mine: totalCount,
              favorites: favoriteCards.length,
              community: communityCount,
              archived: archivedCount,
            }}
            showCommunity={showCommunityGames}
            canImport={canImportLocalProject}
            searchQuery={searchQuery}
            hiddenCount={hiddenCount}
            showTestGames={showTestGames}
            onSearchChange={setSearchQuery}
            onSelect={setActiveView}
            onCreate={() => navigate('/new-game')}
            onImport={() => setImportOpen(true)}
            footer={workbenchFooter}
          />
        )}

        <main
          id="dashboard-project-view"
          role="tabpanel"
          aria-labelledby={flags.newUi ? `dashboard-view-${activeView}` : undefined}
          className={`min-w-0 flex-1 overflow-auto ${flags.newUi ? 'px-8 pb-10 pt-7 [&::-webkit-scrollbar]:hidden' : 'px-10 py-8'}`}
          style={flags.newUi ? { scrollbarWidth: 'none' } : undefined}
        >
          {/* Legacy header row — the new UI uses the persistent top bar above. */}
          {!flags.newUi && (
            <div className="mb-6 flex min-w-0 items-center gap-2.5 overflow-hidden">
              <h1 className="text-xl font-semibold">My Games</h1>
              <DashboardSearchBar value={searchQuery} onChange={setSearchQuery} />
              {hiddenCount > 0 && !showTestGames && (
                <span
                  className="rounded-full bg-white/5 px-2 py-0.5 text-[11px] text-white/30"
                  title="Toggle in Settings → Preferences → Show test games"
                >
                  {hiddenCount} test hidden
                </span>
              )}
              <Button
                variant={showArchived ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setShowArchived((value) => !value)}
                title="View archived games"
              >
                Archived{archivedCount > 0 ? ` ${archivedCount}` : ''}
              </Button>
              {!fullscreenFrame && (
                <div className="ml-auto flex shrink-0 items-center justify-end">
                  {dashboardActions}
                </div>
              )}
            </div>
          )}

          {flags.newUi && !loading && !error && (
            <header className="mb-6">
              <h1
                className="text-[26px] font-semibold tracking-[-0.02em]"
                style={{ color: 'var(--text-1)' }}
              >
                {activeViewTitle}
              </h1>
              {activeViewCount !== undefined && (
                <p className="mt-1 text-[13px]" style={{ color: 'var(--text-3)' }}>
                  {activeViewCount} {activeViewCount === 1 ? 'project' : 'projects'}
                </p>
              )}
            </header>
          )}

          {loading && (
            <div className="flex h-full items-center justify-center text-white/40">
              Loading games...
            </div>
          )}

          {!loading && error && showTransientCloudAuthError && (
            // #3123 — a cloud `game.list@1` auth_failure (-32001) is a
            // transient / pending-rollout blip, NOT an account problem.
            // Present it as a self-healing state with a Retry, never the
            // furnace's account-condemning "not available for this
            // account" text.
            <div
              className="flex h-full flex-col items-center justify-center gap-3 text-center"
              role="status"
              data-testid="dashboard-cloud-auth-transient"
            >
              <p className="text-sm text-white/70">{TRANSIENT_CLOUD_AUTH_TITLE}</p>
              <p className="max-w-sm text-xs text-white/40">{TRANSIENT_CLOUD_AUTH_DETAIL}</p>
              <Button variant="secondary" size="sm" onClick={refetchGames}>
                <RefreshCw size={14} strokeWidth={2} />
                Retry
              </Button>
            </div>
          )}

          {!loading && error && !showTransientCloudAuthError && (
            <div className="flex h-full flex-col items-center justify-center gap-2">
              <p className="text-sm text-red-400">Failed to load games</p>
              <p className="text-xs text-white/40">
                {error instanceof Error ? error.message : String(error)}
              </p>
            </div>
          )}

          {!loading && !error && showingArchived && (
            <ArchivedGamesSection
              preferences={preferences}
              searchQuery={searchQuery}
              showHeading={!flags.newUi}
              onRestore={(entry) => {
                void setArchive({
                  scope: entry.scope,
                  gameId: entry.gameId,
                  ...(entry.scope === 'local' || entry.scope === 'cloud'
                    ? { project: { placement: entry.scope, gameId: entry.gameId } }
                    : {}),
                  repoFullName: entry.repoFullName,
                  archived: false,
                });
              }}
            />
          )}

          {!loading &&
            !error &&
            ((flags.newUi && showingFavorites) || (!flags.newUi && !showArchived)) &&
            searchedFavoriteCards.length > 0 && (
              <DashboardFavoritesSection
                favorites={searchedFavoriteCards}
                showHeading={!flags.newUi}
                onOpenProject={openProject}
                onClone={async (favorite) => {
                  if (!favorite.repoFullName) return;
                  await window.crucible.invoke('game:clone', {
                    project: { placement: 'local', gameId: favorite.gameId },
                    repoFullName: favorite.repoFullName,
                  });
                  invalidateGameLists(queryClient);
                  void queryClient.invalidateQueries({ queryKey: ['game:discover'] });
                }}
                onRemoveFavorite={(favorite) => {
                  void setFavorite({ ...favoritePreferencePayload(favorite), favorite: false });
                }}
                onArchive={(favorite) => {
                  void setArchive({ ...favoritePreferencePayload(favorite), archived: true });
                }}
              />
            )}

          {flags.newUi &&
            !loading &&
            !error &&
            showingFavorites &&
            searchedFavoriteCards.length === 0 && (
              <DashboardEmptyView
                title={searchQuery.trim() ? 'No matching favorites' : 'No favorites yet'}
                detail={
                  searchQuery.trim()
                    ? `No favorites match “${searchQuery}”.`
                    : 'Favorite a project to keep it close at hand.'
                }
                onClearSearch={searchQuery.trim() ? () => setSearchQuery('') : undefined}
              />
            )}

          {/* Legacy empty state — new UI uses the "+" tile inside the grid instead */}
          {!loading && !error && !showArchived && totalCount === 0 && !flags.newUi && (
            <div className="flex h-full flex-col items-center justify-center gap-4">
              <div className="text-6xl text-white/10">&#127918;</div>
              <h2 className="text-lg font-medium text-white/60">No games yet</h2>
              <p className="max-w-sm text-center text-sm text-white/40">
                Create a new game from a prompt, or import an existing local project.
              </p>
              <Button onClick={() => navigate('/new-game')} className="mt-2">
                Add Your First Game
              </Button>
            </div>
          )}

          {/* My Games empty state (new UI) — no games yet, no active search.
            A dedicated block rather than relying on the grid's "+" tile. */}
          {flags.newUi &&
            !loading &&
            !error &&
            showingMyGames &&
            totalCount === 0 &&
            !searchQuery.trim() && (
              <div
                className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-white/[0.12] px-6 py-16 text-center"
                data-testid="dashboard-my-games-empty"
              >
                <h3 className="text-base font-medium" style={{ color: 'var(--text-2)' }}>
                  No games yet
                </h3>
                <p className="text-sm" style={{ color: 'var(--text-3)' }}>
                  Got an idea? Let&apos;s bring it to life.
                </p>
                <Button
                  onClick={() => navigate('/new-game')}
                  data-testid="dashboard-empty-create-game-button"
                  aria-label="Create New Game"
                  className="mt-1"
                >
                  <Plus size={16} strokeWidth={2} />
                  Create New Game
                </Button>
              </div>
            )}

          {/* Game grid — rendered only when there are games to show. */}
          {!loading &&
            !error &&
            showingMyGames &&
            searchCount > 0 &&
            (lazyPath
              ? renderLazyGrid(
                  searchLazy,
                  openProject,
                  flags.newUi,
                  localSetupNeeded,
                  preferences,
                  setFavorite,
                  setArchive,
                  // New UI: no "+" grid tile — the persistent New Game button and
                  // the empty-state CTA are the create affordances. Legacy UI's
                  // tile routes to the live /new-game flow (crucible#3313 — the
                  // legacy CreateGameDialog it used to open is deleted).
                  flags.newUi ? undefined : () => navigate('/new-game'),
                )
              : renderLegacyGrid(
                  searchLegacy,
                  openProject,
                  flags.newUi,
                  localSetupNeeded,
                  preferences,
                  setFavorite,
                  setArchive,
                  // New UI: no "+" grid tile — the persistent New Game button and
                  // the empty-state CTA are the create affordances. Legacy UI's
                  // tile routes to the live /new-game flow (crucible#3313 — the
                  // legacy CreateGameDialog it used to open is deleted).
                  flags.newUi ? undefined : () => navigate('/new-game'),
                ))}

          {/* Search found no matching games */}
          {!loading &&
            !error &&
            showingMyGames &&
            searchQuery.trim() !== '' &&
            searchCount === 0 &&
            filteredCount > 0 && (
              <div className="flex h-48 flex-col items-center justify-center gap-2">
                <p className="text-sm text-white/40">
                  No games matching &ldquo;{searchQuery}&rdquo;
                </p>
                <Button variant="ghost" onClick={() => setSearchQuery('')}>
                  Clear search
                </Button>
              </div>
            )}

          {!loading &&
            !error &&
            showingMyGames &&
            totalCount > 0 &&
            filteredCount === 0 &&
            !searchQuery.trim() && (
              <div className="flex h-full flex-col items-center justify-center gap-4">
                <div className="text-4xl text-white/10">&#128065;</div>
                <h2 className="text-lg font-medium text-white/60">All games are hidden</h2>
                <p className="max-w-sm text-center text-sm text-white/40">
                  {hiddenCount} test game{hiddenCount === 1 ? ' is' : 's are'} hidden. Enable "Show
                  test games" in Settings to reveal them.
                </p>
              </div>
            )}

          {/* Community Games — all games in the org. Deferred until the
            local list has resolved so `game:discover` doesn't fight
            first-card hydration for network. Plan lines 199-201. */}
          {!loading &&
            !error &&
            showCommunityGames &&
            ((flags.newUi && showingCommunity) || (!flags.newUi && !showArchived)) && (
              <div>
                <CommunityGames
                  localGameIds={localGameIds}
                  showTestGames={showTestGames}
                  discovered={communityQuery.data}
                  isLoading={communityWaitingForDiscovery || communityQuery.isLoading}
                  error={communityQuery.error}
                  preferences={preferences}
                  onArchive={setArchive}
                  onFavorite={setFavorite}
                  searchQuery={searchQuery}
                  standalone={flags.newUi}
                />
              </div>
            )}
        </main>
      </div>

      {canImportLocalProject && (
        <ImportGameDialog
          open={importOpen}
          onClose={handleImportClose}
          onImported={handleImported}
          onUseAsContext={handleUseImportAsContext}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// New-game "plus" tile — first item in the game grid (new UI only).
// ---------------------------------------------------------------------------

/**
 * A blank tile shaped like a game card with a centered plus icon.
 * Appears first in the grid and triggers the create-game dialog.
 * Replaces the old header "+ New Game" button and the old empty-state
 * CTA in the new UI.
 */
function NewGameTile({ onClick }: { onClick: () => void }): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Create a new game"
      className="group relative flex w-full cursor-pointer flex-col overflow-hidden rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-crucible-accent"
      style={{
        backgroundColor: 'var(--bg-panel)',
      }}
    >
      {/* Pulsing accent glow. The create-game action is the only entry
          point in the new UI and was easy to miss as a plain dark tile,
          so the border + halo gently pulse to draw the eye. `animate-pulse`
          eases the whole layer's opacity; the hover rule pins it solid so
          it stops pulsing once the user is clearly on it. */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 animate-pulse rounded-2xl group-hover:!opacity-100"
        style={{
          boxShadow: 'inset 0 0 0 1px rgba(124,111,247,0.7), 0 0 22px 1px rgba(124,111,247,0.40)',
        }}
      />

      {/* Ghost spacers mirror the LazyGameCard structure so the tile
          grows to the same intrinsic height at every grid column width —
          regardless of which row it lands in. No explicit px height needed. */}
      <div className="aspect-video w-full" />
      {/* Footer spacer: p-3 (12px top+bottom) + h-6 avatar row = ~48px */}
      <div className="p-3">
        <div className="h-6" />
      </div>

      {/* Accent plus + label — absolutely centred over the ghost
          structure. The circle pulses with the halo and the explicit
          "New game" label removes any doubt about what the tile does. */}
      <span className="absolute inset-0 flex flex-col items-center justify-center gap-2">
        <span
          className="flex h-12 w-12 animate-pulse items-center justify-center rounded-full
            transition-transform duration-[var(--motion-fast)] group-hover:scale-110 group-hover:!opacity-100"
          style={{
            background: 'rgba(124,111,247,0.18)',
            boxShadow: 'inset 0 0 0 1px rgba(124,111,247,0.55)',
          }}
        >
          <Plus
            size={18}
            strokeWidth={2}
            style={{ color: 'var(--color-crucible-accent, #7C6FF7)' }}
          />
        </span>
        <span
          className="text-[11px] font-medium tracking-wide"
          style={{ color: 'var(--color-crucible-accent, #7C6FF7)' }}
        >
          New game
        </span>
      </span>
    </button>
  );
}

function DashboardEmptyView({
  title,
  detail,
  onClearSearch,
}: {
  title: string;
  detail: string;
  onClearSearch?: () => void;
}): React.JSX.Element {
  return (
    <section className="flex min-h-64 flex-col items-center justify-center gap-2 rounded-xl border border-white/[0.08] bg-white/[0.025] px-6 text-center">
      <h2 className="text-[15px] font-medium" style={{ color: 'var(--text-2)' }}>
        {title}
      </h2>
      <p className="text-[13px]" style={{ color: 'var(--text-3)' }}>
        {detail}
      </p>
      {onClearSearch && (
        <Button variant="ghost" size="sm" onClick={onClearSearch} className="mt-2">
          Clear search
        </Button>
      )}
    </section>
  );
}

export function DashboardFavoritesSection({
  favorites,
  onOpenProject,
  onClone,
  onRemoveFavorite,
  onArchive,
  showHeading = true,
}: {
  favorites: DashboardFavoriteCard[];
  onOpenProject: (project: NonNullable<DashboardFavoriteCard['project']>) => void;
  onClone: (favorite: DashboardFavoriteCard) => Promise<void>;
  onRemoveFavorite: (favorite: DashboardFavoriteCard) => void;
  /**
   * Archive a favorited project (#3527). Optional so the section still
   * renders in isolation (unit tests, any future read-only surface); the
   * Archive affordance is simply omitted when it's absent.
   */
  onArchive?: (favorite: DashboardFavoriteCard) => void;
  showHeading?: boolean;
}): React.JSX.Element {
  const [cloningKey, setCloningKey] = useState<string | null>(null);
  const [failedHeroImageKeys, setFailedHeroImageKeys] = useState<Set<string>>(() => new Set());

  return (
    <section className="mb-8" aria-label="Favorite games">
      {showHeading && (
        <div className="mb-4 flex items-center gap-2">
          <Star size={15} className="text-[var(--color-brand)]" fill="currentColor" />
          <h2 className="f-title" style={{ color: 'var(--text-2)' }}>
            Favorites
          </h2>
        </div>
      )}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {favorites.map((favorite) => {
          const project = favorite.project;
          const canOpenProject = project !== undefined;
          const cloning = cloningKey === favorite.key;
          const heroFailureKey =
            favorite.heroImage !== undefined
              ? `${favorite.key}:${favorite.heroImage}`
              : favorite.key;
          const heroImageFailed = failedHeroImageKeys.has(heroFailureKey);
          return (
            <article
              key={favorite.key}
              className="group overflow-hidden rounded-xl border border-white/[0.08] bg-white/[0.035] transition-colors hover:bg-white/[0.055]"
            >
              <div
                role={canOpenProject ? 'button' : undefined}
                tabIndex={canOpenProject ? 0 : undefined}
                onClick={() => {
                  if (project) onOpenProject(project);
                }}
                onKeyDown={(e) => {
                  if (!project) return;
                  if (e.target !== e.currentTarget) return;
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onOpenProject(project);
                  }
                }}
                className={canOpenProject ? 'cursor-pointer' : undefined}
                aria-label={canOpenProject ? `Open ${favorite.displayName}` : undefined}
              >
                <div className="relative aspect-video bg-white/[0.04]">
                  {favorite.heroImage && !heroImageFailed ? (
                    <img
                      src={favorite.heroImage}
                      alt={favorite.displayName}
                      className="h-full w-full object-cover"
                      loading="lazy"
                      decoding="async"
                      onError={() => {
                        setFailedHeroImageKeys((prev) => {
                          if (prev.has(heroFailureKey)) return prev;
                          const next = new Set(prev);
                          next.add(heroFailureKey);
                          return next;
                        });
                      }}
                    />
                  ) : (
                    <GenreFallback
                      genreKey={inferGenreKey(favorite.displayName, favorite.description)}
                      alt={favorite.displayName}
                      className="h-full w-full object-cover"
                    />
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveFavorite(favorite);
                    }}
                    className="absolute right-2 top-2 h-7 w-7 rounded-md bg-black/60 text-[var(--color-brand)] backdrop-blur-sm hover:bg-black/80"
                    title="Remove from favorites"
                    aria-label={`Unfavorite ${favorite.displayName}`}
                  >
                    <Star size={14} fill="currentColor" strokeWidth={1.8} />
                  </Button>
                </div>
                <div className="space-y-2 p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h3 className="truncate text-sm font-medium text-white/85">
                        {favorite.displayName}
                      </h3>
                      <p className="text-[11px] text-white/35">
                        {favorite.source === 'local'
                          ? favorite.repoFullName
                            ? 'Local copy'
                            : 'Local game'
                          : favorite.source === 'cloud'
                            ? 'Cloud game'
                            : favorite.source === 'community'
                              ? 'Community game'
                              : 'Saved favorite'}
                      </p>
                    </div>
                  </div>
                  {favorite.description && (
                    <p className="line-clamp-2 text-xs text-white/40">{favorite.description}</p>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-2 px-3 pb-3">
                {project ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(event) => {
                      event.stopPropagation();
                      onOpenProject(project);
                    }}
                  >
                    Open
                  </Button>
                ) : favorite.repoFullName ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={cloning}
                    onClick={async () => {
                      setCloningKey(favorite.key);
                      try {
                        await onClone(favorite);
                      } finally {
                        setCloningKey(null);
                      }
                    }}
                  >
                    {cloning ? 'Cloning...' : 'Clone to edit'}
                  </Button>
                ) : null}
                {favorite.repoUrl && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openExternalUrl(favorite.repoUrl)}
                  >
                    GitHub
                  </Button>
                )}
                <FavoriteCardAuxActions favorite={favorite} onArchive={onArchive} />
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

/**
 * The auxiliary per-favorite actions: open the repo in the editor,
 * generate artwork, archive (#3527).
 *
 * The Favorites card shipped with only Open / Clone to edit / GitHub, so
 * favoriting a game silently cost you the affordances the SAME game's
 * card carries in the grid below. That inverts the point of favoriting —
 * you had to scroll the unfiltered list to find the project just to open
 * its repo, which is the action reached for most often.
 *
 * The IPC calls and capability gates are the same ones `<LazyGameCard>` /
 * `<GameCard>` use, so the two surfaces can't disagree about when an
 * action is available. They render as always-visible footer buttons
 * rather than the cards' hover-only icon overlay because this card's
 * action row is already always-visible, and the hero corner it would
 * occupy is taken by the unfavorite star.
 *
 * Own component (not inlined in the `.map`) because the generate-hero
 * in-flight + error state is per-card and hooks can't live in a loop.
 */
function FavoriteCardAuxActions({
  favorite,
  onArchive,
}: Readonly<{
  favorite: DashboardFavoriteCard;
  onArchive?: (favorite: DashboardFavoriteCard) => void;
}>): React.JSX.Element | null {
  const queryClient = useQueryClient();
  const capabilities = useClientCapabilities();
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);
  // The 5s error auto-dismiss must not outlive the card — a favorite
  // that unmounts (unfavorited, filtered out by search) while the timer
  // is pending would otherwise setState on an unmounted component.
  const genErrorTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (genErrorTimer.current !== null) clearTimeout(genErrorTimer.current);
    },
    [],
  );

  const project = favorite.project;
  // Same gates as the grid cards: the editor needs real local files, and
  // hero generation is a local-only pipeline. A community favorite that
  // was never cloned has no `project` at all, so it gets none of these.
  const canOpenInEditor =
    project !== undefined &&
    project.placement !== 'cloud' &&
    capabilities.hasLocalFilesystem &&
    canInvokeChannel(capabilities, 'game:open-in-editor');
  const canGenerateHero =
    project?.placement === 'local' && canInvokeChannel(capabilities, 'game:generate-hero');
  const canArchive = project !== undefined && onArchive !== undefined;

  if (!canOpenInEditor && !canGenerateHero && !canArchive) return null;

  const handleOpenInEditor = (event: React.MouseEvent): void => {
    event.stopPropagation();
    if (!canOpenInEditor || !project) return;
    // Editor-not-found surfaces on the separate `editor:error` IPC that
    // the dashboard-level toast effect listens to. Other rejections
    // (gameId validation, gamesDir failure, spawn error) would land as
    // unhandled rejections — warn instead, same as the grid cards.
    void window.crucible
      .invoke('game:open-in-editor', { placement: project.placement, gameId: project.gameId })
      .catch((err) => console.warn('[game:open-in-editor] failed:', err));
  };

  const handleGenerateHero = async (event: React.MouseEvent): Promise<void> => {
    event.stopPropagation();
    if (generating || !canGenerateHero || !project) return;
    setGenerating(true);
    setGenError(null);
    try {
      await window.crucible.invoke('game:generate-hero', {
        project: { placement: project.placement, gameId: project.gameId },
        gameName: favorite.displayName,
      });
      // Touch both list queries so the new hero lands on whichever
      // dashboard path is active, and the favorites card re-derives its
      // own `heroImage` from the refreshed list.
      invalidateGameLists(queryClient);
    } catch (err) {
      setGenError(err instanceof Error ? err.message : String(err));
      if (genErrorTimer.current !== null) clearTimeout(genErrorTimer.current);
      genErrorTimer.current = setTimeout(() => setGenError(null), 5000);
    } finally {
      setGenerating(false);
    }
  };

  const handleArchive = (event: React.MouseEvent): void => {
    event.stopPropagation();
    if (!canArchive || !onArchive) return;
    if (confirm(projectArchiveConfirmPrompt(favorite.displayName))) onArchive(favorite);
  };

  // Labelled pills, no decorative glyphs. A bare icon in a pill row is
  // ambiguous — the sparkles/wand mark in particular reads as "generate
  // with AI" wherever it appears, so it can't also stand for a specific
  // action here. The only glyph left is the in-flight spinner, which is
  // state rather than decoration.
  return (
    <>
      {canOpenInEditor && (
        <Button
          variant="ghost"
          size="sm"
          onClick={handleOpenInEditor}
          title="Open the project folder in Cursor"
          aria-label={`Open ${favorite.displayName} in Cursor`}
        >
          Open in Cursor
        </Button>
      )}
      {canGenerateHero && (
        <Button
          variant="ghost"
          size="sm"
          disabled={generating}
          onClick={(event) => void handleGenerateHero(event)}
          className="inline-flex items-center gap-1"
          title={generating ? 'Generating...' : 'Generate artwork'}
          aria-label={
            generating
              ? `Generating artwork for ${favorite.displayName}`
              : `Generate artwork for ${favorite.displayName}`
          }
        >
          {generating && (
            <div className="h-3 w-3 animate-spin rounded-full border border-white/20 border-t-white/80" />
          )}
          {generating ? 'Generating...' : 'Generate art'}
        </Button>
      )}
      {canArchive && (
        <Button
          variant="ghost"
          size="sm"
          onClick={handleArchive}
          title="Archive game"
          aria-label={`Archive ${favorite.displayName}`}
        >
          Archive
        </Button>
      )}
      {genError && (
        <p className="w-full text-[11px] text-red-300" role="status">
          {genError}
        </p>
      )}
    </>
  );
}

function ArchivedGamesSection({
  preferences,
  searchQuery,
  showHeading = true,
  onRestore,
}: {
  preferences: DashboardPreferences | undefined;
  searchQuery: string;
  showHeading?: boolean;
  onRestore: (entry: DashboardArchiveEntry) => void;
}): React.JSX.Element {
  const query = searchQuery.trim().toLowerCase();
  const archived = (preferences?.archived ?? [])
    .filter((entry) => {
      if (!query) return true;
      const label = entry.displayName ?? entry.repoFullName ?? entry.gameId;
      return label.toLowerCase().includes(query);
    })
    .sort((a, b) => (a.displayName ?? a.gameId).localeCompare(b.displayName ?? b.gameId));

  if (archived.length === 0) {
    return (
      <section className="flex h-64 flex-col items-center justify-center gap-2 rounded-xl border border-white/[0.08] bg-white/[0.025]">
        <Archive size={24} className="text-white/20" />
        <h2 className="text-sm font-medium text-white/55">No archived games</h2>
        <p className="text-xs text-white/35">
          {query
            ? 'No archived games match your search.'
            : 'Archived games will appear here so you can restore them.'}
        </p>
      </section>
    );
  }

  return (
    <section aria-label="Archived games">
      {showHeading && (
        <div className="mb-4 flex items-center gap-2">
          <Archive size={15} className="text-white/45" />
          <h2 className="f-title" style={{ color: 'var(--text-2)' }}>
            Archived Games
          </h2>
        </div>
      )}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {archived.map((entry) => {
          const label = entry.displayName ?? entry.repoFullName ?? entry.gameId;
          return (
            <article
              key={dashboardPreferenceKey(entry)}
              className="overflow-hidden rounded-xl border border-white/[0.08] bg-white/[0.035]"
            >
              <div className="aspect-video bg-white/[0.04]">
                {entry.heroImage ? (
                  <img
                    src={entry.heroImage}
                    alt={label}
                    className="h-full w-full object-cover opacity-70"
                    loading="lazy"
                    decoding="async"
                  />
                ) : (
                  <GenreFallback
                    genreKey={inferGenreKey(label, entry.description)}
                    alt={label}
                    className="h-full w-full object-cover opacity-70"
                  />
                )}
              </div>
              <div className="space-y-3 p-3">
                <div>
                  <h3 className="truncate text-sm font-medium text-white/75">{label}</h3>
                  <p className="text-[11px] text-white/35">
                    {entry.scope === 'community' ? 'Community game' : 'Local game'} · archived{' '}
                    {formatTimeAgo(entry.archivedAt)}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onRestore(entry)}
                    className="inline-flex items-center gap-1"
                  >
                    <RotateCcw size={12} strokeWidth={1.8} />
                    Restore
                  </Button>
                  {entry.repoUrl && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openExternalUrl(entry.repoUrl)}
                      className="inline-flex items-center gap-1"
                    >
                      <ExternalLink size={12} strokeWidth={1.8} />
                      GitHub
                    </Button>
                  )}
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

/**
 * Renders the legacy `<GameCard>` grid driven by the full `GameSummary`
 * payload from the eager `game:list` IPC. Kept verbatim from the
 * pre-lazy Dashboard so the rollback path
 * (`CRUCIBLE_LAZY_DASHBOARD_LOAD=0`) is a true one-knob switch — no
 * behavioural drift between code paths.
 */
function renderLegacyGrid(
  filteredGames: ProjectCardSummary[],
  onOpenProject: (game: ProjectCardSummary) => void,
  newUi: boolean,
  localSetupNeeded: boolean,
  preferences: DashboardPreferences | undefined,
  onFavorite: (input: DashboardFavoriteSetInput) => Promise<void>,
  onArchive: (input: DashboardArchiveSetInput) => Promise<void>,
  onCreateGame?: () => void,
): React.JSX.Element {
  // Glint system: only the three most-recently-updated cards
  // get the edge-trace ambient highlight (per the design spec).
  // Sort by lastModified desc; cards with no usable timestamp
  // (`""` or unparseable) are treated as oldest so they never
  // crowd out a real recent edit. Stagger the animation phase
  // via data-fresh-index 0/1/2.
  const ts = (g: ProjectCardSummary): number => {
    const v = g.lastModified;
    if (!v) return 0;
    const parsed = new Date(v).getTime();
    return Number.isFinite(parsed) ? parsed : 0;
  };
  const sortedByRecency = [...filteredGames].sort((a, b) => ts(b) - ts(a));
  // Only mark a card "fresh" if it has a real timestamp —
  // otherwise the top three cards in render order would all
  // glow on a fresh `crucible list` where nothing's been
  // edited.
  const freshIds = new Map(
    sortedByRecency
      .slice(0, 3)
      .filter((g) => ts(g) > 0)
      .map((g, i) => [`${g.placement}:${g.gameId}`, i] as const),
  );
  return (
    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {newUi && onCreateGame && (
        <div className="relative grid rounded-2xl">
          <NewGameTile onClick={onCreateGame} />
        </div>
      )}
      {[...filteredGames]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((game) => {
          const freshIndex = freshIds.get(`${game.placement}:${game.gameId}`);
          const freshProps =
            freshIndex !== undefined
              ? { 'data-fresh': 'true', 'data-fresh-index': String(freshIndex) }
              : {};
          return (
            <div
              key={`${game.placement}:${game.gameId}`}
              className={`relative grid ${newUi ? 'rounded-2xl' : 'rounded-lg'}`}
              {...freshProps}
            >
              <GameCard
                game={game}
                onClick={() => onOpenProject(game)}
                newUi={newUi}
                localSetupNeeded={localSetupNeeded && game.placement === 'local'}
                favorite={isDashboardFavorited(preferences, {
                  scope: game.placement,
                  gameId: game.gameId,
                })}
                onFavorite={(favorite) =>
                  onFavorite({ ...localSummaryPreferencePayload(game), favorite })
                }
                onArchive={() =>
                  onArchive({ ...localSummaryPreferencePayload(game), archived: true })
                }
              />
            </div>
          );
        })}
    </div>
  );
}

/**
 * Renders the lazy `<LazyGameCard>` grid driven by `GameSummaryLite`.
 * Each card mounts in placeholder state and hydrates its per-card
 * status + badges only when it scrolls into view (200 px rootMargin).
 * Plan §"Layer 2 — Per-card hydration".
 *
 * Glint "freshness" is sorted by `createdAt` here — `lastModified`
 * isn't on the lite payload (it would force a per-game `fs.stat` in
 * the cheap-fast list, defeating the point). `createdAt` is a
 * reasonable proxy for "newest cards glow"; once a user scrolls to a
 * card and the lite-status hydrate lands we don't retroactively
 * reorder the glint set — keeping the visual stable mid-scroll
 * matters more than precise recency.
 */
function renderLazyGrid(
  filteredGames: ProjectCardSummaryLite[],
  onOpenProject: (game: ProjectCardSummaryLite) => void,
  newUi: boolean,
  localSetupNeeded: boolean,
  preferences: DashboardPreferences | undefined,
  onFavorite: (input: DashboardFavoriteSetInput) => Promise<void>,
  onArchive: (input: DashboardArchiveSetInput) => Promise<void>,
  onCreateGame?: () => void,
): React.JSX.Element {
  const ts = (g: ProjectCardSummaryLite): number => {
    const v = g.createdAt;
    if (!v) return 0;
    const parsed = new Date(v).getTime();
    return Number.isFinite(parsed) ? parsed : 0;
  };
  const sortedByRecency = [...filteredGames].sort((a, b) => ts(b) - ts(a));
  const freshIds = new Map(
    sortedByRecency
      .slice(0, 3)
      .filter((g) => ts(g) > 0)
      .map((g, i) => [`${g.placement}:${g.gameId}`, i] as const),
  );
  return (
    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {newUi && onCreateGame && (
        <div className="relative grid rounded-2xl">
          <NewGameTile onClick={onCreateGame} />
        </div>
      )}
      {[...filteredGames]
        .sort((a, b) => a.displayName.localeCompare(b.displayName))
        .map((game) => {
          const freshIndex = freshIds.get(`${game.placement}:${game.gameId}`);
          const freshProps =
            freshIndex !== undefined
              ? { 'data-fresh': 'true', 'data-fresh-index': String(freshIndex) }
              : {};
          return (
            <div
              key={`${game.placement}:${game.gameId}`}
              className={`relative grid ${newUi ? 'rounded-2xl' : 'rounded-lg'}`}
              {...freshProps}
            >
              <LazyGameCard
                game={game}
                onClick={() => onOpenProject(game)}
                newUi={newUi}
                localSetupNeeded={localSetupNeeded && game.placement === 'local'}
                favorite={isDashboardFavorited(preferences, {
                  scope: game.placement,
                  gameId: game.gameId,
                })}
                onFavorite={(favorite) =>
                  onFavorite({ ...localLitePreferencePayload(game), favorite })
                }
                onArchive={() => onArchive({ ...localLitePreferencePayload(game), archived: true })}
              />
            </div>
          );
        })}
    </div>
  );
}

const STATUS_COLOURS: Record<GameSummary['status'], string> = {
  draft: 'bg-white/20 text-white/60',
  building: 'bg-info/20 text-info',
  prototype: 'bg-warning/20 text-warning',
  live: 'bg-success/20 text-success',
  error: 'bg-error/20 text-error',
};

/**
 * Maps game lifecycle badge → StatusPill intent.
 * Used by the tile-image overlay (proof call site for #2403).
 */
const GAME_STATUS_INTENT: Record<
  GameStatusBadge,
  { label: string; intent: 'neutral' | 'info' | 'warning' | 'success' | 'error' }
> = {
  draft: { label: 'Draft', intent: 'neutral' },
  building: { label: 'Building', intent: 'info' },
  prototype: { label: 'Prototype', intent: 'warning' },
  live: { label: 'Live', intent: 'success' },
  error: { label: 'Error', intent: 'error' },
};

/**
 * Issue #462 — the GameCard used to wrap the whole card surface in
 * a native `<button>`, which silently nested SIX inner
 * `<div role="button">` action affordances (generate-artwork,
 * open-in-cursor, delete, status, TemplateUpdateBadge,
 * InterviewInProgressBadge) inside an interactive ancestor with the
 * same role. That's an HTML / WCAG violation: an interactive element
 * cannot have interactive descendants with the same `button` role.
 * Screen readers report the nested-role conflict and keyboard
 * navigation gets confused; some browsers strip the inner buttons'
 * click handlers.
 *
 * The fix unflattens the hierarchy. The OUTER card surface (the "open
 * this game" affordance) is now a `<div role="button" tabIndex={0}>`
 * with explicit `onClick` + `onKeyDown` Enter/Space handlers. The
 * inner action affordances remain as they were (each
 * `stopPropagation`s its own click so the card doesn't open). All
 * keyboard a11y survives: the outer card is tab-focusable; pressing
 * Enter or Space opens the workspace.
 *
 * Exported so the dedicated a11y regression test
 * (`Dashboard.gamecard-a11y.test.tsx`) can mount this in isolation
 * without dragging the whole Dashboard's IPC + router surface in.
 */
/**
 * Lite card surface for the dashboard lazy-load path. Mounts in
 * placeholder state from a `GameSummaryLite` payload (gameId +
 * displayName + createdAt only) and hydrates the rest only when the
 * card scrolls into view (`useCardVisibility` with 200 px rootMargin).
 *
 * Plan: `docs/dashboard-lazy-load-plan.md` §"Layer 2 — Per-card
 * hydration". The three badges (`InstallStateChip`,
 * `TemplateUpdateBadge`, `InterviewInProgressBadge`) and the status-
 * lite hydrate all share the same `isVisible` boolean — cards below
 * the fold stay inert; scrolling near them flips the flag and every
 * child query fires at once.
 *
 * Hero image: synthesised from the `game-asset://` protocol (no IPC
 * needed; the protocol handler resolves disk paths server-side) and
 * tagged `loading="lazy"` so the browser defers off-viewport fetches.
 * Falls back to `<GenreFallback>` if the asset 404s.
 */
/** Green circle icon button shown on new-UI tiles when a template update is available. */
function UpdateIconButton({
  gameId,
  project,
  enabled,
  onClick,
}: {
  gameId: string;
  project: ProjectRef;
  enabled?: boolean;
  onClick: (e: React.MouseEvent) => void;
}): React.JSX.Element | null {
  const { data } = useTemplateUpdateCheck(gameId, { enabled, project });
  if (!data?.hasUpdate) return null;
  const tone = data.isBreaking
    ? 'bg-warning/20 text-warning hover:bg-warning/30'
    : 'bg-success/20 text-success hover:bg-success/30';
  return (
    <div
      role="button"
      tabIndex={0}
      title={
        data.isBreaking
          ? `Breaking update: v${data.current} → v${data.latest}`
          : `Update available: v${data.current} → v${data.latest}`
      }
      onClick={(e) => {
        e.stopPropagation();
        onClick(e);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.stopPropagation();
          e.preventDefault();
          onClick(e as unknown as React.MouseEvent);
        }
      }}
      className={`shrink-0 cursor-pointer rounded-full p-1.5 transition-colors ${tone}`}
    >
      {data.isBreaking ? (
        <AlertTriangle size={12} strokeWidth={2.5} />
      ) : (
        <RefreshCw size={12} strokeWidth={2.5} />
      )}
    </div>
  );
}

export function LazyGameCard({
  game,
  onClick,
  newUi = false,
  favorite = false,
  onFavorite,
  onArchive,
  localSetupNeeded = false,
}: {
  game: ProjectCardSummaryLite;
  onClick: () => void;
  newUi?: boolean;
  favorite?: boolean;
  onFavorite?: (favorite: boolean) => Promise<void>;
  onArchive?: () => Promise<void>;
  localSetupNeeded?: boolean;
}): React.JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const capabilities = useClientCapabilities();
  const project: ProjectRef = { placement: game.placement, gameId: game.gameId };
  const canOpenInEditor =
    game.placement !== 'cloud' &&
    capabilities.hasLocalFilesystem &&
    canInvokeChannel(capabilities, 'game:open-in-editor');
  const canGenerateHero =
    game.placement === 'local' && canInvokeChannel(capabilities, 'game:generate-hero');
  const cardRef = useRef<HTMLDivElement>(null);
  const { isVisible } = useCardVisibility(cardRef, { rootMargin: '200px' });
  const localBadgeQueriesEnabled = isVisible && game.placement === 'local';
  const status = useGameStatusLite(
    { placement: game.placement, gameId: game.gameId },
    {
      enabled: localBadgeQueriesEnabled,
    },
  );
  const statusBadge: GameStatusBadge = status.data?.statusBadge ?? 'draft';
  // Mirrors legacy GameCard: the TemplateUpdateBadge needs a click
  // target that opens the UpdateTemplateModal. Without this state +
  // the rendered modal below, the badge's required `onClick` prop has
  // no useful destination and the user has no way to act on a flagged
  // template drift on the lazy path.
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  // Hover-only action affordances — same set the legacy `<GameCard>`
  // ships (favorite, generate artwork, open in cursor, archive/delete). The
  // lazy card initially shipped without these and the dashboard's
  // affordances regressed on the lazy path: no way to regenerate art,
  // open the folder in an editor, or hide/remove a local clone. Restored
  // for parity. State mirrors legacy: `generating` gates the
  // generate-hero IPC against double-clicks, `genError` shows a
  // 5-second toast on failure.
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);

  // Hero URL is resolved on the main side and carried on the lite
  // payload (`GameSummaryLite.heroImage`) — same shape the legacy
  // `<GameCard>` consumes from `game:list`. The earlier code in this
  // function tried to synthesise `game-asset://<id>/hero.webp` from
  // the renderer, but the protocol handler in main/index.ts expects
  // an encoded absolute path (line 5448), not a `<id>/<file>`
  // relative, so every hero 403'd on the lazy path. Resolving in
  // main keeps the protocol contract honest and avoids duplicating
  // the bundled-tile / on-disk / genre-fallback chain in the
  // renderer.
  const [imgFailed, setImgFailed] = useState(false);
  const heroUrl = game.heroImage;

  const handleOpenInCursor = (e: React.MouseEvent): void => {
    e.stopPropagation();
    if (!canOpenInEditor) return;
    // `game:open-in-editor` emits `editor:error` over a separate IPC
    // for editor-not-found cases (the dashboard-level toast effect
    // at the top of this file catches those). Other rejections — the
    // M-7 `assertValidGameId` throw added in a1bd9d0, a
    // `getGamesDir()` failure, an editor spawn error — bypass that
    // channel and would otherwise surface as unhandled rejections.
    // Catch and warn so the console shows the failure but we don't
    // double up on user-visible toasts. Cursor Bugbot LOW on f00b178.
    void window.crucible
      .invoke('game:open-in-editor', { placement: game.placement, gameId: game.gameId })

      .catch((err) => console.warn('[game:open-in-editor] failed:', err));
  };

  const handleGenerateHero = async (e: React.MouseEvent): Promise<void> => {
    e.stopPropagation();
    if (generating || !canGenerateHero) return;
    setGenerating(true);
    setGenError(null);
    try {
      await window.crucible.invoke('game:generate-hero', {
        project: { placement: game.placement, gameId: game.gameId },
        gameName: game.displayName,
      });
      // Touch BOTH list queries so the lazy path picks up the new
      // hero URL on its next paint, same as the legacy path would.
      invalidateGameLists(queryClient);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setGenError(msg);
      setTimeout(() => setGenError(null), 5000);
    } finally {
      setGenerating(false);
    }
  };

  const handleFavorite = (e: React.MouseEvent): void => {
    e.stopPropagation();
    void onFavorite?.(!favorite);
  };

  const handleArchive = (e: React.MouseEvent): void => {
    e.stopPropagation();
    if (confirm(projectArchiveConfirmPrompt(game.displayName))) {
      void onArchive?.();
    }
  };

  const handleDelete = (e: React.MouseEvent): void => {
    e.stopPropagation();
    const copy = projectDeleteCopy({
      placement: game.placement,
      name: game.displayName,
      imported: game.imported,
    });
    if (confirm(copy.prompt)) {
      // `game:delete` can reject for the same reasons as the editor
      // open above (validation throw, FS failure). Catch so a delete
      // failure doesn't surface as an unhandled rejection; the user
      // will retry when they notice the card is still in the grid.
      window.crucible
        .invoke('game:delete', { placement: game.placement, gameId: game.gameId })
        .then(
          () => invalidateGameLists(queryClient),

          (err) => console.warn('[game:delete] failed:', err),
        );
    }
  };

  const handleCardKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <>
      <div
        ref={cardRef}
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={handleCardKeyDown}
        data-testid="gamecard-surface"
        aria-label={`Open ${game.displayName}`}
        aria-disabled="false"
        className={
          newUi
            ? 'card-gradient group relative flex h-full cursor-pointer flex-col overflow-hidden rounded-2xl transition-all hover:brightness-[1.08]'
            : 'group relative flex h-full cursor-pointer flex-col overflow-hidden rounded-lg border border-white/10 bg-white/[0.03] transition-all hover:border-white/20 hover:bg-white/[0.05]'
        }
        style={
          newUi
            ? {
                boxShadow: 'var(--hairline), var(--shadow-card)',
                transitionTimingFunction: 'var(--ease-flora)',
                transitionDuration: 'var(--motion-fast)',
              }
            : undefined
        }
      >
        <div className="relative aspect-video w-full bg-white/5">
          {heroUrl && !imgFailed ? (
            <img
              src={heroUrl}
              alt={game.displayName}
              className="h-full w-full object-cover"
              loading="lazy"
              decoding="async"
              onError={() => setImgFailed(true)}
            />
          ) : (
            <GenreFallback
              genreKey={inferGenreKey(game.displayName, game.description)}
              alt={game.displayName}
              className="h-full w-full object-cover"
            />
          )}
          {generating && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/60 backdrop-blur-sm">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-white/20 border-t-crucible-accent" />
              <span className="text-xs font-medium text-white/70">Generating artwork...</span>
            </div>
          )}
          {genError && (
            <div className="absolute inset-x-0 bottom-0 bg-red-900/90 px-3 py-2 text-xs text-red-200 backdrop-blur-sm">
              {genError}
            </div>
          )}
          {/* Status pill — new UI only, always visible, hover controls overlay on top.
              Proof call site for #2403: migrated from the old STATUS_PILL inline
              render to the shared StatusPill primitive. */}
          {newUi &&
            (() => {
              const { label, intent } = GAME_STATUS_INTENT[statusBadge];
              return (
                <StatusPill
                  intent={intent}
                  glass
                  className="absolute left-2 top-2"
                  style={{ background: 'rgba(10,10,10,0.75)' }}
                >
                  {label}
                </StatusPill>
              );
            })()}
        </div>
        {onFavorite && (
          <div
            role="button"
            tabIndex={0}
            onClick={handleFavorite}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                handleFavorite(e as unknown as React.MouseEvent);
              }
            }}
            title={favorite ? 'Remove from favorites' : 'Add to favorites'}
            aria-label={
              favorite ? `Unfavorite ${game.displayName}` : `Favorite ${game.displayName}`
            }
            className={`${newUi ? 'left-2 top-11' : 'left-2 top-2'} absolute flex h-7 w-7 items-center justify-center rounded-md bg-black/60 ${favorite ? 'text-[var(--color-brand)]' : 'text-white/50'} opacity-0 backdrop-blur-sm transition-opacity hover:bg-black/80 hover:text-[var(--color-brand)] group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100`}
          >
            <Star size={14} strokeWidth={1.8} fill={favorite ? 'currentColor' : 'none'} />
          </div>
        )}
        {/* Hover action affordances — mirror legacy GameCard L688-L761
            verbatim so the lazy path doesn't regress card-level
            affordances (generate art, open in cursor, archive/delete). Each
            is `<div role="button">` (nested role inside the outer
            card's role="button"); each `stopPropagation`s so the
            card-open doesn't fire behind it. opacity-0 →
            group-hover:opacity-100 keeps the surface clean until the
            mouse lands. */}
        {/* Legacy UI: generate-hero button at top-left. New UI uses the
            same spot for the status pill above, so suppress this
            affordance under the newUi gate. */}
        {!newUi && !game.imported && canGenerateHero && (
          <div
            role="button"
            tabIndex={0}
            onClick={handleGenerateHero}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                // `preventDefault` blocks the Space-bar page-scroll the
                // browser would otherwise trigger on a focused role=button.
                // Macroscope MED on eb1de4a.
                e.preventDefault();
                e.stopPropagation();
                void handleGenerateHero(e as unknown as React.MouseEvent);
              }
            }}
            title={generating ? 'Generating...' : 'Generate artwork'}
            aria-label={
              generating
                ? `Generating artwork for ${game.displayName}`
                : `Generate artwork for ${game.displayName}`
            }
            className={`absolute ${onFavorite ? 'left-2 top-11' : 'left-2 top-2'} flex h-7 w-7 items-center justify-center rounded-md bg-black/60 text-white/50 opacity-0 backdrop-blur-sm transition-opacity hover:bg-black/80 hover:text-white/90 group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100`}
          >
            {generating ? (
              <div className="h-3.5 w-3.5 animate-spin rounded-full border border-white/20 border-t-white/80" />
            ) : (
              <Sparkles size={14} strokeWidth={1.8} />
            )}
          </div>
        )}
        {(canOpenInEditor || game.placement === 'cloud') && (
          <div
            role="button"
            tabIndex={canOpenInEditor ? 0 : -1}
            aria-disabled={!canOpenInEditor}
            onClick={canOpenInEditor ? handleOpenInCursor : undefined}
            onKeyDown={(e) => {
              if (!canOpenInEditor) return;
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleOpenInCursor(e as unknown as React.MouseEvent);
              }
            }}
            title={canOpenInEditor ? 'Open in Cursor' : 'Cloud project files stay in Cloud.'}
            className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-md bg-black/60 text-white/50 opacity-0 backdrop-blur-sm transition-opacity hover:bg-black/80 hover:text-white/90 group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100 aria-disabled:cursor-not-allowed aria-disabled:opacity-40"
          >
            <CursorEditorGlyph />
          </div>
        )}
        {/* New UI: generate-hero button at right-2 top-11. Legacy
            UI keeps main's delete button in this spot — see below. */}
        {newUi && canGenerateHero && (
          <div
            role="button"
            tabIndex={0}
            onClick={handleGenerateHero}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                void handleGenerateHero(e as unknown as React.MouseEvent);
              }
            }}
            title={generating ? 'Generating...' : 'Generate artwork'}
            aria-label={
              generating
                ? `Generating artwork for ${game.displayName}`
                : `Generate artwork for ${game.displayName}`
            }
            className="absolute right-2 top-11 flex h-7 w-7 items-center justify-center rounded-md bg-black/60 text-white/50 opacity-0 backdrop-blur-sm transition-opacity hover:bg-black/80 hover:text-white/90 group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100"
          >
            {generating ? (
              <div className="h-3.5 w-3.5 animate-spin rounded-full border border-white/20 border-t-white/80" />
            ) : (
              <Sparkles size={14} strokeWidth={1.8} />
            )}
          </div>
        )}
        {/* Legacy UI: archive/delete action at right-2 top-11 (smart
            label — Archive game when preferences are available, otherwise
            Remove from Foundry for imported or Delete locally). Wrapped in
            !newUi so the new UI's generate-
            hero button takes the same spot without overlap. */}
        {newUi && onArchive && (
          <div
            role="button"
            tabIndex={0}
            onClick={handleArchive}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                handleArchive(e as unknown as React.MouseEvent);
              }
            }}
            title="Archive game"
            aria-label={`Archive ${game.displayName}`}
            className="absolute right-2 top-20 flex h-7 w-7 items-center justify-center rounded-md bg-black/60 text-white/50 opacity-0 backdrop-blur-sm transition-opacity hover:bg-black/80 hover:text-white/90 group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100"
          >
            <Archive size={14} strokeWidth={1.8} />
          </div>
        )}
        {!newUi && (
          <div
            role="button"
            tabIndex={0}
            onClick={onArchive ? handleArchive : handleDelete}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                (onArchive ? handleArchive : handleDelete)(e as unknown as React.MouseEvent);
              }
            }}
            title={
              onArchive
                ? 'Archive game'
                : projectDeleteCopy({
                    placement: game.placement,
                    name: game.displayName,
                    imported: game.imported,
                  }).label
            }
            aria-label={
              onArchive
                ? `Archive ${game.displayName}`
                : projectDeleteCopy({
                    placement: game.placement,
                    name: game.displayName,
                    imported: game.imported,
                  }).ariaLabel
            }
            className={`absolute right-2 top-11 flex h-7 w-7 items-center justify-center rounded-md bg-black/60 text-white/50 opacity-0 backdrop-blur-sm transition-opacity ${onArchive ? 'hover:bg-black/80 hover:text-white/90' : 'hover:bg-red-900/80 hover:text-red-300'} group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100`}
          >
            {onArchive ? (
              <Archive size={14} strokeWidth={1.8} />
            ) : (
              <svg
                width="14"
                height="14"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4 4l8 8M12 4l-8 8"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            )}
          </div>
        )}
        <div className="flex flex-1 flex-col gap-2 p-3">
          {newUi ? (
            <>
              <div className="flex items-start gap-2">
                <h3
                  className="min-w-0 flex-1 truncate text-sm leading-6"
                  style={{ color: 'var(--text-1)', fontWeight: 500 }}
                >
                  {game.displayName}
                </h3>
                {localSetupNeeded && (
                  <span className="shrink-0 rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] text-amber-200/80">
                    Setup needed
                  </span>
                )}
                <div className="flex shrink-0 items-center gap-1">
                  <InstallStateChip
                    gameId={game.gameId}
                    project={project}
                    variant="icon"
                    enabled={isVisible && game.placement === 'local'}
                  />
                  <UpdateIconButton
                    gameId={game.gameId}
                    project={project}
                    enabled={isVisible && game.placement === 'local'}
                    onClick={() => setShowUpdateModal(true)}
                  />
                </div>
              </div>
              <div className="flex min-h-6 items-center gap-2">
                {/* Contributor facepile */}
                {game.contributors && game.contributors.length > 0 ? (
                  <div className="flex shrink-0 items-center">
                    {game.contributors.slice(0, 5).map((c, i) => (
                      <img
                        key={c.login}
                        src={c.avatarUrl}
                        alt={c.login}
                        title={c.login}
                        className="h-6 w-6 rounded-full ring-2 ring-black"
                        style={{ marginLeft: i === 0 ? 0 : '-8px', zIndex: i }}
                      />
                    ))}
                  </div>
                ) : null}
                {/* Branch or last-commit date */}
                {(() => {
                  const git = status.data?.localGit;
                  if (!git?.exists) return <div className="flex-1" />;
                  const branch = git.branch && git.branch !== 'main' ? git.branch : null;
                  const label =
                    branch ?? (git.lastCommitDate ? formatTimeAgo(git.lastCommitDate) : null);
                  if (!label) return <div className="flex-1" />;
                  return (
                    <span
                      className="min-w-0 flex-1 truncate text-right text-[11px] tabular-nums"
                      style={{ color: 'var(--text-3)' }}
                      title={
                        branch ? `Branch: ${git.branch}` : `Last commit: ${git.lastCommitDate}`
                      }
                    >
                      {branch ? `⎇ ${label}` : label}
                    </span>
                  );
                })()}
              </div>
              {game.clonedFrom && (
                <div
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    openExternalUrl(repoUrlFromFullName(game.clonedFrom));
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      e.stopPropagation();
                      openExternalUrl(repoUrlFromFullName(game.clonedFrom));
                    }
                  }}
                  className="flex items-center gap-1 text-[11px] text-white/35 transition-colors hover:text-white/65"
                  title={`Open ${game.clonedFrom} on GitHub`}
                  aria-label={`Open GitHub repo for ${game.displayName}`}
                >
                  <ExternalLink size={11} strokeWidth={1.8} />
                  <span>Open GitHub repo</span>
                </div>
              )}
            </>
          ) : (
            <>
              {/* Legacy UI — main's card info layout, preserved verbatim. */}
              <div className="flex items-center gap-2">
                <span
                  className={`inline-flex h-2 w-2 shrink-0 rounded-full ${STATUS_COLOURS[statusBadge].split(' ')[0]}`}
                />
                <h3 className="truncate text-sm font-medium text-white/90">{game.displayName}</h3>
                {game.imported && (
                  <span className="shrink-0 rounded-full bg-sky-500/15 px-1.5 py-0.5 text-[10px] font-medium text-sky-300/80">
                    Imported
                  </span>
                )}
                {game.clonedFrom && (
                  <span
                    className="shrink-0 rounded-full bg-violet-500/15 px-1.5 py-0.5 text-[10px] font-medium text-violet-300/80"
                    title={`Cloned from ${game.clonedFrom}`}
                  >
                    Local copy
                  </span>
                )}
              </div>
              {game.clonedFrom && (
                <div
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    openExternalUrl(repoUrlFromFullName(game.clonedFrom));
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      e.stopPropagation();
                      openExternalUrl(repoUrlFromFullName(game.clonedFrom));
                    }
                  }}
                  className="flex items-center gap-1 text-[11px] text-white/35 transition-colors hover:text-white/65"
                  title={`Open ${game.clonedFrom} on GitHub`}
                  aria-label={`Open GitHub repo for ${game.displayName}`}
                >
                  <ExternalLink size={11} strokeWidth={1.8} />
                  <span>Open GitHub repo</span>
                </div>
              )}
              {/* TemplateUpdateBadge + InterviewInProgressBadge are nested
                  role="button" descendants inside the card's role="button"
                  ancestor — same shape the legacy `<GameCard>` ships
                  (#462 docblock). Each badge `stopPropagation`s its own
                  click so the card doesn't open behind it; keyboard a11y
                  survives the same way. Pulling them out as siblings (a la
                  `<InstallStateChip>`) would require five absolutely-
                  positioned wrappers in this corner of the card and isn't
                  warranted while legacy carries the same nesting. */}
              <div className="flex items-center gap-1.5">
                {!game.imported && (
                  <TemplateUpdateBadge
                    gameId={game.gameId}
                    project={project}
                    enabled={isVisible && game.placement === 'local'}
                    onClick={() => setShowUpdateModal(true)}
                  />
                )}
                <InterviewInProgressBadge
                  gameId={game.gameId}
                  project={project}
                  enabled={isVisible}
                  onClick={onClick}
                />
              </div>
            </>
          )}
        </div>
      </div>
      {/* Render the update modal outside the card surface so its
          inner buttons aren't nested inside the card's interactive
          ancestor. The modal is fixed-positioned so its JSX location
          doesn't affect visual placement. Mirrors the legacy
          `<GameCard>` pattern (line 853 in the unmerged tree). */}
      <UpdateTemplateModal
        gameId={game.gameId}
        open={showUpdateModal}
        onClose={() => setShowUpdateModal(false)}
        onOpenWorkspace={() =>
          navigate(
            `/workspace/${game.placement ?? capabilities.defaultProjectPlacement ?? 'local'}/${game.gameId}`,
          )
        }
      />
    </>
  );
}

export function GameCard({
  game,
  onClick,
  newUi = false,
  favorite = false,
  onFavorite,
  onArchive,
  localSetupNeeded = false,
}: {
  game: ProjectCardSummary;
  onClick: () => void;
  newUi?: boolean;
  favorite?: boolean;
  onFavorite?: (favorite: boolean) => Promise<void>;
  onArchive?: () => Promise<void>;
  localSetupNeeded?: boolean;
}): React.JSX.Element {
  const queryClient = useQueryClient();
  const capabilities = useClientCapabilities();
  const project: ProjectRef = { placement: game.placement, gameId: game.gameId };
  const canOpenInEditor =
    game.placement !== 'cloud' &&
    capabilities.hasLocalFilesystem &&
    canInvokeChannel(capabilities, 'game:open-in-editor');
  const canGenerateHero =
    game.placement === 'local' && canInvokeChannel(capabilities, 'game:generate-hero');
  // Issue #613: when the Update modal hits CRUCIBLE-312 (pending
  // conflict resolution) the user needs a one-click route to the
  // game's Workspace so they land on the existing
  // <ConflictResolutionCard>. Reusing the same `/workspace/:gameId`
  // route the card's outer `onClick` resolves to, instead of
  // plumbing the click handler down — the modal needs to call this
  // from a state-transition path, not a click event.
  const navigate = useNavigate();
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);
  const [showStatus, setShowStatus] = useState(false);
  // #471 — stable id linking the status toggle to the collapsible status
  // detail panel below, so `aria-controls` on the toggle points at the
  // panel and `aria-expanded` announces the toggled state to screen readers.
  const statusPanelId = `gamecard-status-${game.gameId}`;
  const [gameStatus, setGameStatus] = useState<GameStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  // Issue #637 — ambient deploy-status snapshot for the expanded status
  // panel. Subscribe only while the panel is open (gate the gameId): the
  // status panel's `game:status` invoke is what starts the main-side
  // watcher, so a collapsed card never registers a listener and never
  // triggers polling. Flag-OFF the watcher never starts so this stays
  // undefined and the pill renders nothing.
  const deployStatus = useDeployStatus(showStatus ? game.gameId : undefined);
  // #603 — track <img> load failure so we can swap to <GenreFallback>
  // when the URL the main process resolved is no longer on disk (e.g.
  // the game's local hero image was deleted between game:list and
  // render). Reset whenever heroImage changes so a fresh URL gets a
  // fresh try; this matches the same reset shape used by
  // <CommunityGameCard> below for consistency.
  const [imgFailed, setImgFailed] = useState(false);
  useEffect(() => {
    setImgFailed(false);
  }, [game.heroImage]);

  const handleOpenInCursor = (e: React.MouseEvent): void => {
    e.stopPropagation();
    if (!canOpenInEditor) return;
    void window.crucible
      .invoke('game:open-in-editor', {
        placement: game.placement,
        gameId: game.gameId,
      })
      .catch((err) => console.warn('[game:open-in-editor] failed:', err));
  };

  const handleGenerateHero = async (e: React.MouseEvent): Promise<void> => {
    e.stopPropagation();
    if (generating || !canGenerateHero) return;
    setGenerating(true);
    setGenError(null);
    try {
      await window.crucible.invoke('game:generate-hero', {
        project: { placement: game.placement, gameId: game.gameId },
        gameName: game.name,
      });
      invalidateGameLists(queryClient);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setGenError(msg);
      setTimeout(() => setGenError(null), 5000);
    } finally {
      setGenerating(false);
    }
  };

  const handleFavorite = (e: React.MouseEvent): void => {
    e.stopPropagation();
    void onFavorite?.(!favorite);
  };

  const handleArchive = (e: React.MouseEvent): void => {
    e.stopPropagation();
    if (confirm(projectArchiveConfirmPrompt(game.name))) {
      void onArchive?.();
    }
  };

  const handleDelete = (e: React.MouseEvent): void => {
    e.stopPropagation();
    const copy = projectDeleteCopy({
      placement: game.placement,
      name: game.name,
      imported: game.imported,
    });
    if (confirm(copy.prompt)) {
      window.crucible
        .invoke('game:delete', { placement: game.placement, gameId: game.gameId })
        .then(
          () => invalidateGameLists(queryClient),

          (err) => console.warn('[game:delete] failed:', err),
        );
    }
  };

  // #462 — outer card is a div-role=button (not a native <button>) so
  // the inner action affordances (info, delete, open-in-cursor,
  // generate-art, badges) can stay as themselves without nesting
  // interactive role="button" inside an interactive ancestor.
  const handleCardKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      // Only react when the event targets the card surface itself,
      // not when it bubbles up from a focused inner button/anchor.
      // Inner buttons already `stopPropagation()` on click, but
      // some keyboard events arrive here (Space on a focused inner
      // role="button" div, etc.). Guard on currentTarget so we
      // don't double-trigger.
      if (e.target !== e.currentTarget) return;
      e.preventDefault();
      onClick();
    }
  };

  return (
    <>
      {/* Codex YELLOW fold (#447 / #457) ∩ #462 hardening: the
        `<InstallStateChip variant="pill">` used to live INSIDE the outer
        GameCard surface, next to the game name. That nested an
        interactive element inside the card's interactive ancestor —
        invalid HTML (when the outer was `<button>`) and still an ARIA
        nested-role violation now that #462 promoted the outer to a
        `<div role="button">`. Either way the chip needs to escape the
        interactive ancestor.

        The chip now renders as a SIBLING of the outer card surface,
        positioned absolutely against the parent grid wrapper
        (Dashboard's grid item provides `relative` + `display: grid`,
        see line 253). The fragment here doesn't add a DOM node, so
        both the absolute-positioned chip and the auto-stretching card
        div remain direct children of the grid wrapper. Same visual
        prominence (corner badge); no nested interactive descendants;
        card-click still navigates because the chip's click handler
        calls `stopPropagation`. Position is top-right below the
        action toolbar so it doesn't visually overlap the archive/delete
        affordance. Mirrors the same out-of-button trick the
        comment further down uses for `<UpdateTemplateModal>`. */}
      <div className="pointer-events-auto absolute right-2 top-28 z-10">
        <InstallStateChip
          gameId={game.gameId}
          project={project}
          variant="pill"
          enabled={game.placement === 'local'}
        />
      </div>
      <div
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={handleCardKeyDown}
        data-testid="gamecard-surface"
        aria-label={`Open ${game.name}`}
        aria-disabled="false"
        className={
          newUi
            ? 'card-gradient group relative flex flex-col overflow-hidden rounded-2xl transition-all cursor-pointer hover:brightness-[1.08] focus:outline-none focus-visible:ring-2 focus-visible:ring-crucible-accent'
            : 'group relative flex flex-col overflow-hidden rounded-lg border border-white/10 bg-crucible-surface transition-colors hover:border-white/20 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-crucible-accent'
        }
        style={
          newUi
            ? {
                boxShadow: 'var(--hairline), var(--shadow-card)',
                transitionTimingFunction: 'var(--ease-flora)',
                transitionDuration: 'var(--motion-fast)',
              }
            : undefined
        }
      >
        {/* Hero image / placeholder.
          #603: fall back to <GenreFallback> when (a) the main process
          couldn't resolve any hero image (cloned game with no committed
          art, no bundled tile, no bundled genre WebP), or (b) the
          resolved URL was set but the load failed. The genre key is
          inferred from `name` + `description` so the placeholder
          matches the game's vibe instead of the previous one-cube-fits-
          all default-prototype.webp. */}
        <div className="relative aspect-video w-full bg-white/5">
          {game.heroImage && !imgFailed ? (
            <img
              src={game.heroImage}
              alt={game.name}
              className="h-full w-full object-cover"
              // Dashboard lazy-load plan §"Hero image hydrate": let the
              // browser defer off-viewport image fetches + decode them
              // off the main thread. Free win on 110-card grids.
              loading="lazy"
              decoding="async"
              onError={() => setImgFailed(true)}
            />
          ) : (
            <GenreFallback
              genreKey={inferGenreKey(game.name, game.description)}
              alt={game.name}
              className="h-full w-full object-cover"
            />
          )}
          {generating && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/60 backdrop-blur-sm">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-white/20 border-t-crucible-accent" />
              <span className="text-xs font-medium text-white/70">Generating artwork...</span>
            </div>
          )}
          {genError && (
            <div className="absolute inset-x-0 bottom-0 bg-red-900/90 px-3 py-2 text-xs text-red-200 backdrop-blur-sm">
              {genError}
            </div>
          )}
        </div>

        {onFavorite && (
          <div
            role="button"
            tabIndex={0}
            onClick={handleFavorite}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
                e.preventDefault();
                handleFavorite(e as unknown as React.MouseEvent);
              }
            }}
            title={favorite ? 'Remove from favorites' : 'Add to favorites'}
            aria-label={favorite ? `Unfavorite ${game.name}` : `Favorite ${game.name}`}
            className={`left-2 top-2 absolute flex h-7 w-7 items-center justify-center rounded-md bg-black/60 ${favorite ? 'text-[var(--color-brand)]' : 'text-white/50'} opacity-0 backdrop-blur-sm transition-opacity hover:bg-black/80 hover:text-[var(--color-brand)] group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100`}
          >
            <Star size={14} strokeWidth={1.8} fill={favorite ? 'currentColor' : 'none'} />
          </div>
        )}

        {/* New UI: generate-artwork button at right-2 top-11 (DOM-first
          so it's first in tab order, #462). */}
        {newUi && (
          <div
            role="button"
            tabIndex={0}
            onClick={handleGenerateHero}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
                void handleGenerateHero(e as unknown as React.MouseEvent);
              }
            }}
            title={generating ? 'Generating...' : 'Generate artwork'}
            aria-label={
              generating
                ? `Generating artwork for ${game.name}`
                : `Generate artwork for ${game.name}`
            }
            className="absolute right-2 top-11 flex h-7 w-7 items-center justify-center rounded-md bg-black/60 text-white/50 opacity-0 backdrop-blur-sm transition-opacity hover:bg-black/80 hover:text-white/90 group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100"
          >
            {generating ? (
              <div className="h-3.5 w-3.5 animate-spin rounded-full border border-white/20 border-t-white/80" />
            ) : (
              <Sparkles size={14} strokeWidth={1.8} />
            )}
          </div>
        )}
        {/* Legacy UI: main's generate-artwork button at left-2 top-2. */}
        {!newUi && !game.imported && canGenerateHero && (
          <div
            role="button"
            tabIndex={0}
            onClick={handleGenerateHero}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
                void handleGenerateHero(e as unknown as React.MouseEvent);
              }
            }}
            title={generating ? 'Generating...' : 'Generate artwork'}
            aria-label={
              generating
                ? `Generating artwork for ${game.name}`
                : `Generate artwork for ${game.name}`
            }
            className={`absolute ${onFavorite ? 'left-2 top-11' : 'left-2 top-2'} flex h-7 w-7 items-center justify-center rounded-md bg-black/60 text-white/50 opacity-0 backdrop-blur-sm transition-opacity hover:bg-black/80 hover:text-white/90 group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100`}
          >
            {generating ? (
              <div className="h-3.5 w-3.5 animate-spin rounded-full border border-white/20 border-t-white/80" />
            ) : (
              <Sparkles size={14} strokeWidth={1.8} />
            )}
          </div>
        )}

        {/* Open in Cursor button (top-right corner) */}
        {(canOpenInEditor || game.placement === 'cloud') && (
          <div
            role="button"
            tabIndex={canOpenInEditor ? 0 : -1}
            aria-disabled={!canOpenInEditor}
            onClick={canOpenInEditor ? handleOpenInCursor : undefined}
            onKeyDown={(e) => {
              if (!canOpenInEditor) return;
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
                handleOpenInCursor(e as unknown as React.MouseEvent);
              }
            }}
            title={canOpenInEditor ? 'Open in Cursor' : 'Cloud project files stay in Cloud.'}
            className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-md bg-black/60 text-white/50 opacity-0 backdrop-blur-sm transition-opacity hover:bg-black/80 hover:text-white/90 group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100 aria-disabled:cursor-not-allowed aria-disabled:opacity-40"
          >
            <CursorEditorGlyph />
          </div>
        )}

        {/* Delete button (#462 — a11y: tabIndex=0, stopPropagation so the
          card-open doesn't fire). Position differs between UIs: new UI
          places it at right-2 top-20 (separate from generate-hero at
          top-11); legacy UI places it at right-2 top-11 (no generate
          button on the right column there). */}
        {newUi && (
          <div
            role="button"
            tabIndex={0}
            onClick={onArchive ? handleArchive : handleDelete}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
                (onArchive ? handleArchive : handleDelete)(e as unknown as React.MouseEvent);
              }
            }}
            title={
              onArchive
                ? 'Archive game'
                : projectDeleteCopy({
                    placement: game.placement,
                    name: game.name,
                    imported: game.imported,
                  }).label
            }
            aria-label={
              onArchive
                ? `Archive ${game.name}`
                : projectDeleteCopy({
                    placement: game.placement,
                    name: game.name,
                    imported: game.imported,
                  }).ariaLabel
            }
            className="absolute right-2 top-20 flex h-7 w-7 items-center justify-center rounded-md bg-black/60 text-white/50 opacity-0 backdrop-blur-sm transition-opacity hover:bg-black/80 hover:text-white/90 group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100"
          >
            {onArchive ? (
              <Archive size={14} strokeWidth={1.8} />
            ) : (
              <svg
                width="14"
                height="14"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                xmlns="http://www.w3.org/2000/svg"
              >
                <polyline points="3,4 13,4" />
                <path d="M6,4V2h4v2" />
                <path d="M5,4l1,10h4l1-10" />
              </svg>
            )}
          </div>
        )}
        {!newUi && (
          <div
            role="button"
            tabIndex={0}
            onClick={onArchive ? handleArchive : handleDelete}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
                e.preventDefault();
                (onArchive ? handleArchive : handleDelete)(e as unknown as React.MouseEvent);
              }
            }}
            title={
              onArchive
                ? 'Archive game'
                : projectDeleteCopy({
                    placement: game.placement,
                    name: game.name,
                    imported: game.imported,
                  }).label
            }
            aria-label={
              onArchive
                ? `Archive ${game.name}`
                : projectDeleteCopy({
                    placement: game.placement,
                    name: game.name,
                    imported: game.imported,
                  }).ariaLabel
            }
            className={`absolute right-2 top-11 flex h-7 w-7 items-center justify-center rounded-md bg-black/60 text-white/50 opacity-0 backdrop-blur-sm transition-opacity ${onArchive ? 'hover:bg-black/80 hover:text-white/90' : 'hover:bg-red-900/80 hover:text-red-300'} group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100`}
          >
            {onArchive ? (
              <Archive size={14} strokeWidth={1.8} />
            ) : (
              <svg
                width="14"
                height="14"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4 4l8 8M12 4l-8 8"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            )}
          </div>
        )}

        {/* Info */}
        <div className="flex flex-1 flex-col gap-1 p-4">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-1.5 min-w-0">
              <h3
                className={`truncate font-medium group-hover:text-white${newUi ? '' : ' text-white/90'}`}
                style={newUi ? { color: 'var(--text-1)' } : undefined}
              >
                {game.name}
              </h3>
              {localSetupNeeded && (
                <span className="shrink-0 rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] text-amber-200/80">
                  Setup needed
                </span>
              )}
              {game.category && (
                <span className="shrink-0 rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-medium text-amber-400/80">
                  {game.category}
                </span>
              )}
              {newUi ? (
                <UpdateIconButton
                  gameId={game.gameId}
                  project={project}
                  enabled={game.placement === 'local'}
                  onClick={() => setShowUpdateModal(true)}
                />
              ) : (
                <>
                  {game.imported && (
                    <span className="shrink-0 rounded-full bg-sky-500/15 px-1.5 py-0.5 text-[10px] font-medium text-sky-300/80">
                      Imported
                    </span>
                  )}
                  {game.clonedFrom && (
                    <span
                      className="shrink-0 rounded-full bg-violet-500/15 px-1.5 py-0.5 text-[10px] font-medium text-violet-300/80"
                      title={`Cloned from ${game.clonedFrom}`}
                    >
                      Local copy
                    </span>
                  )}
                  {!game.imported && (
                    <TemplateUpdateBadge
                      gameId={game.gameId}
                      project={project}
                      enabled={game.placement === 'local'}
                      onClick={() => setShowUpdateModal(true)}
                    />
                  )}
                  <InterviewInProgressBadge
                    gameId={game.gameId}
                    project={project}
                    onClick={onClick}
                  />
                  {/* Codex YELLOW fold (#447 / #457): `<InstallStateChip
                    variant="pill">` used to render HERE — inline next to the
                    game name. That nested the chip's `<button>` inside the
                    outer GameCard `<button>` (invalid HTML, real a11y
                    regression). Moved to the GameCard's relative wrapper as a
                    sibling-overlay positioned absolutely. See the wrapper at
                    the top of this function for the placement. */}
                </>
              )}
            </div>
            {game.clonedFrom && (
              <div
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  openExternalUrl(repoUrlFromFullName(game.clonedFrom));
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    openExternalUrl(repoUrlFromFullName(game.clonedFrom));
                  }
                }}
                className="flex w-fit items-center gap-1 text-[11px] text-white/35 transition-colors hover:text-white/65"
                title={`Open ${game.clonedFrom} on GitHub`}
                aria-label={`Open GitHub repo for ${game.name}`}
              >
                <ExternalLink size={11} strokeWidth={1.8} />
                <span>Open GitHub repo</span>
              </div>
            )}
            <div className="flex items-center gap-1">
              <div
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!showStatus && !gameStatus && !statusLoading) {
                    setStatusLoading(true);
                    window.crucible
                      .invoke('game:status', { placement: game.placement, gameId: game.gameId })
                      .then((s) => {
                        setGameStatus(s);
                        setStatusLoading(false);
                      })
                      .catch(() => setStatusLoading(false));
                  }
                  setShowStatus(!showStatus);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.stopPropagation();
                    if (!showStatus && !gameStatus && !statusLoading) {
                      setStatusLoading(true);
                      window.crucible
                        .invoke('game:status', { placement: game.placement, gameId: game.gameId })
                        .then((s) => {
                          setGameStatus(s);
                          setStatusLoading(false);
                        })
                        .catch(() => setStatusLoading(false));
                    }
                    setShowStatus(!showStatus);
                  }
                }}
                title="View status"
                aria-expanded={showStatus}
                aria-controls={statusPanelId}
                className="flex h-5 w-5 items-center justify-center rounded text-white/30 transition-colors hover:bg-white/10 hover:text-white/60"
              >
                <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                  <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.2" />
                  <path
                    d="M8 7v4M8 5.5v.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
              <span
                className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLOURS[game.status]}`}
              >
                {game.status}
              </span>
            </div>
          </div>
          <p className="text-xs text-white/40">{formatTimeAgo(game.lastModified)}</p>
        </div>

        {/* Status detail panel */}
        {showStatus && (
          <div
            id={statusPanelId}
            className="border-t border-white/10 p-3"
            onClick={(e) => e.stopPropagation()}
          >
            {statusLoading && <p className="text-xs text-white/30">Loading status...</p>}
            {gameStatus && <GameStatusPanel status={gameStatus} deployStatus={deployStatus} />}
            {!statusLoading && !gameStatus && (
              <p className="text-xs text-white/30">Could not load status</p>
            )}
          </div>
        )}
      </div>
      {/* Render the update modal outside the card surface so its inner
        <button>s aren't nested inside an interactive ancestor. The
        modal is fixed-positioned so the JSX location doesn't affect
        visual placement. (Comment preserved from when the outer card
        was a <button>; #462 swapped that for div role="button" but
        the modal-outside-card invariant still matters for the modal's
        own interactive children.) */}
      <UpdateTemplateModal
        gameId={game.gameId}
        open={showUpdateModal}
        onClose={() => setShowUpdateModal(false)}
        // Issue #613: route the user to the game's Workspace on
        // CRUCIBLE-312 (pending conflict resolution) so they land on
        // the existing `<ConflictResolutionCard>`. The modal closes
        // itself before invoking this callback so the navigate
        // doesn't race the modal's exit animation.
        onOpenWorkspace={() =>
          navigate(
            `/workspace/${game.placement ?? capabilities.defaultProjectPlacement ?? 'local'}/${game.gameId}`,
          )
        }
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Import Game Dialog
// ---------------------------------------------------------------------------

type ImportStage = 'picking' | 'confirming' | 'importing' | 'success' | 'error';

const IMPORT_KIND_LABELS: Record<ImportGameProjectKind, string> = {
  crucible: 'Foundry project',
  'hello-weekend': 'hello-weekend game',
  vgf: 'VGF game',
  'design-docs': 'Build context',
  'source-project': 'Unrecognized source project',
  'invalid-metadata': 'Invalid Foundry metadata',
  unsupported: 'Unrecognized folder',
};

function isImportableProjectKind(kind: ImportGameProjectKind): boolean {
  return kind === 'crucible' || kind === 'hello-weekend' || kind === 'vgf';
}

function importPreviewSummary(preview: ImportGamePreview): string {
  if (preview.canImport) {
    return preview.hasFoundryState
      ? 'Foundry will register this folder in place and preserve its existing build state.'
      : 'Foundry will register this folder in place and add the metadata it needs to open as an existing game.';
  }
  if (preview.kind === 'design-docs') {
    return 'These files can help shape a new game, but they are not a runnable project to import.';
  }
  return preview.unsupportedReason ?? 'Foundry cannot import this folder.';
}

export function ImportGameDialog({
  open = true,
  onClose,
  onImported,
  onUseAsContext,
  canImportLocalProject = true,
}: {
  /**
   * crucible#3332 (Bugbot round 2) — the caller's "the user wants an import
   * flow active right now" signal, DISTINCT from whether the dialog is
   * currently drawn on screen. A caller that persistently mounts this
   * component (see NewGame.tsx, which keeps it mounted across a tier flip so
   * an in-flight `game:import:commit` isn't orphaned) uses `open` to gate the
   * reset-to-a-fresh-attempt effect below AND the render itself; a caller
   * that ALSO wants to visually hide it for an unrelated reason (a tier
   * flip) does so by wrapping this component in a `display: none` container
   * rather than by flipping `open` — flipping `open` deliberately restarts
   * the flow, hiding does not. Defaults to `true` for the common
   * conditionally-mounted case (mount = open), matching the a11y test below.
   */
  open?: boolean;
  onClose: () => void;
  onImported: (gameId: string) => void;
  onUseAsContext?: () => void;
  canImportLocalProject?: boolean;
}): React.JSX.Element | null {
  const queryClient = useQueryClient();
  const [stage, setStage] = useState<ImportStage>('picking');
  const [preview, setPreview] = useState<ImportGamePreview | null>(null);
  const [gameId, setGameId] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [errorState, setErrorState] = useState<SerialisedError | null>(null);
  const [navigateAfterImportId, setNavigateAfterImportId] = useState<string | null>(null);
  const previewIsImportableProject = preview ? isImportableProjectKind(preview.kind) : false;
  const previewHasEditableGameIdIssue = Boolean(
    preview &&
    previewIsImportableProject &&
    !preview.canImport &&
    preview.blockingIssue === 'game-id-conflict',
  );
  const hasEditedConflictingGameId = Boolean(
    previewHasEditableGameIdIssue && gameId.trim() !== preview?.gameId,
  );
  const canSubmitImport = Boolean(
    preview &&
    previewIsImportableProject &&
    (preview.canImport || hasEditedConflictingGameId) &&
    gameId.trim() &&
    displayName.trim(),
  );

  // crucible#3332 round 2 — every `open` transition to true (the initial
  // mount, or a deliberate reopen after an explicit close) starts a FRESH
  // attempt rather than resuming whatever a previous attempt left behind.
  // This is what lets a persistently-mounted caller reuse one instance
  // across many open/close cycles without carrying stale preview/error
  // state into the next click of "Import".
  useEffect(() => {
    if (!open) return;
    setStage('picking');
    setPreview(null);
    setGameId('');
    setDisplayName('');
    setErrorState(null);
    setNavigateAfterImportId(null);
  }, [open]);

  useEffect(() => {
    let cancelled = false;
    if (!open) return;
    if (stage !== 'picking') return;
    if (!canImportLocalProject) return;
    window.crucible.invoke('game:import:pick-directory').then(
      (result) => {
        if (cancelled) return;
        if (!result) {
          onClose();
          return;
        }
        setPreview(result);
        setGameId(result.gameId);
        setDisplayName(result.displayName);
        setStage('confirming');
      },
      (err) => {
        if (cancelled) return;
        setErrorState(unflattenIpcError(err));
        setStage('error');
      },
    );
    return () => {
      cancelled = true;
    };
  }, [open, stage, onClose, canImportLocalProject]);

  // Defer navigation so the success state is visible; clear on unmount
  // so closing the dialog before the delay does not call onImported.
  //
  // crucible#3332 round 2 (Bugbot) — deliberately NOT gated on `open` (or on
  // whether the caller currently draws this component at all). A caller can
  // hide this dialog (a tier flip mid-`importing`) without tearing the
  // component down; hiding it must not lose a commit that's already in
  // flight. Because this effect has no `open` dependency, it fires the
  // moment `stage` reaches 'success' regardless of visibility, so a
  // successful import always reaches its workspace handoff.
  useEffect(() => {
    if (stage !== 'success' || !navigateAfterImportId) return;
    const timer = setTimeout(() => onImported(navigateAfterImportId), 900);
    return () => clearTimeout(timer);
  }, [stage, navigateAfterImportId, onImported]);

  const handleImport = async (): Promise<void> => {
    if (!canImportLocalProject) return;
    if (!preview || !canSubmitImport) return;
    setStage('importing');
    setErrorState(null);
    try {
      const result = await window.crucible.invoke('game:import:commit', {
        selectedPath: preview.selectedPath,
        gameId: gameId.trim(),
        displayName: displayName.trim(),
      });
      invalidateGameLists(queryClient);
      setNavigateAfterImportId(result.gameId);
      setStage('success');
    } catch (err) {
      setErrorState(unflattenIpcError(err));
      setStage('error');
    }
  };

  // crucible#3332 round 2 — placed after every hook above (Rules of Hooks):
  // a caller that keeps this component persistently mounted (NewGame.tsx)
  // relies on `open: false` rendering nothing WITHOUT skipping any effect
  // registration, so the success/navigate effect above keeps running for an
  // in-flight commit even while this returns null.
  if (!open) return null;

  return (
    <div className="app-region-no-drag fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-lg rounded-xl border border-white/10 bg-crucible-surface p-6 shadow-2xl">
        {!canImportLocalProject && (
          <div className="flex flex-col gap-4 py-4">
            <h2 className="text-lg font-semibold">Local import unavailable</h2>
            <p className="text-sm text-white/55">
              Local project import is available in Local Foundry.
            </p>
            <div className="flex justify-end">
              <Button variant="secondary" onClick={onClose}>
                Close
              </Button>
            </div>
          </div>
        )}

        {canImportLocalProject && stage === 'picking' && (
          <div className="flex flex-col items-center gap-3 py-6">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-white/20 border-t-crucible-accent" />
            <p className="text-sm text-white/60">Opening folder picker...</p>
          </div>
        )}

        {canImportLocalProject && stage === 'confirming' && preview && (
          <div className="flex flex-col gap-4">
            <div>
              <h2 className="text-lg font-semibold">Import Existing Game</h2>
              <p className="mt-1 break-all text-xs text-white/40">{preview.selectedPath}</p>
            </div>

            <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm text-white/60">Detected</span>
                <span className="rounded-full bg-white/10 px-2 py-0.5 text-xs text-white/70">
                  {IMPORT_KIND_LABELS[preview.kind]}
                </span>
              </div>
              <p
                className={`mt-2 text-sm ${preview.canImport || hasEditedConflictingGameId || preview.kind === 'design-docs' ? 'text-white/60' : 'text-red-300'}`}
              >
                {hasEditedConflictingGameId
                  ? 'Foundry will revalidate this Game ID when you import.'
                  : importPreviewSummary(preview)}
              </p>
              {preview.kind === 'design-docs' && (
                <p className="mt-2 text-xs text-white/45">
                  Return to the new-build screen and use Add context to attach the documents and
                  screenshots you want Foundry to use.
                </p>
              )}
            </div>

            {previewIsImportableProject && (
              <div className="rounded-lg border border-sky-400/20 bg-sky-400/[0.08] p-3 text-xs text-sky-100/75">
                <p>
                  Foundry will use this folder in place. Importing does not create a copy, a new
                  repository, or a new deployment.
                </p>
                {preview.gitRemoteUrl && (
                  <>
                    <p className="mt-2 break-all">
                      <span className="text-sky-100/50">Git origin:</span> {preview.gitRemoteUrl}
                    </p>
                    <p className="mt-2">
                      Changes published later may update this repository and deployments connected
                      to it.
                    </p>
                  </>
                )}
              </div>
            )}

            {previewIsImportableProject && (
              <>
                <div>
                  <label htmlFor="import-game-display-name" className="block text-sm text-white/60">
                    Display Name
                  </label>
                  <Input
                    id="import-game-display-name"
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    className="mt-1"
                    disabled={!preview.canImport && !previewHasEditableGameIdIssue}
                  />
                </div>

                <div>
                  <label htmlFor="import-game-id" className="block text-sm text-white/60">
                    Game ID
                  </label>
                  <Input
                    id="import-game-id"
                    type="text"
                    value={gameId}
                    onChange={(e) => setGameId(e.target.value)}
                    className="mt-1 font-mono"
                    disabled={!preview.canImport && !previewHasEditableGameIdIssue}
                  />
                  {previewHasEditableGameIdIssue && (
                    <span className="mt-1 block text-xs text-white/45">
                      Enter a valid, unused Game ID to continue.
                    </span>
                  )}
                </div>
              </>
            )}

            {preview.warnings.length > 0 && (
              <div className="rounded-lg border border-amber-500/20 bg-amber-500/10 p-3">
                {preview.warnings.map((warning) => (
                  <p key={warning} className="text-xs text-amber-100/80">
                    {warning}
                  </p>
                ))}
              </div>
            )}

            <div className="flex justify-end gap-3">
              <Button
                variant="ghost"
                onClick={preview.kind === 'design-docs' ? (onUseAsContext ?? onClose) : onClose}
              >
                {preview.kind === 'design-docs' ? 'Back to new build' : 'Cancel'}
              </Button>
              {previewIsImportableProject && (
                <Button onClick={handleImport} disabled={!canSubmitImport}>
                  Import
                </Button>
              )}
            </div>
          </div>
        )}

        {canImportLocalProject && stage === 'importing' && (
          <LoadingOverlay message="Importing game..." isVisible />
        )}

        {canImportLocalProject && stage === 'success' && (
          <div className="flex flex-col items-center gap-3 py-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-500/20">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M5 13l4 4L19 7"
                  stroke="#22c55e"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <h2 className="text-lg font-semibold">Game Imported</h2>
          </div>
        )}

        {canImportLocalProject && stage === 'error' && errorState && (
          <div className="flex flex-col gap-4 py-4">
            <h2 className="text-lg font-semibold">Import Failed</h2>
            <CrucibleErrorState
              error={errorState}
              onRetry={() => {
                setErrorState(null);
                setStage('picking');
              }}
            />
            <div className="flex justify-end">
              <Button variant="ghost" onClick={onClose}>
                Close
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Community Games — all games across the org
// ---------------------------------------------------------------------------

function CommunityGames({
  localGameIds,
  showTestGames,
  discovered,
  isLoading,
  error,
  preferences,
  onArchive,
  onFavorite,
  searchQuery = '',
  standalone = false,
}: {
  localGameIds: string[];
  showTestGames: boolean;
  discovered?: DiscoveredGame[];
  isLoading: boolean;
  error: unknown;
  preferences: DashboardPreferences | undefined;
  onArchive: (input: DashboardArchiveSetInput) => Promise<void>;
  onFavorite: (input: DashboardFavoriteSetInput) => Promise<void>;
  searchQuery?: string;
  standalone?: boolean;
}): React.JSX.Element | null {
  const navigate = useNavigate();

  // When the user defers their GitHub PAT (Tier-2 first-run rework allows
  // this — the PAT moved out of onboarding), `game:discover` throws
  // "GitHub token not set..." at the IPC boundary. Codex flagged that the
  // previous implementation hid that failure entirely, so the Community
  // Games section just vanished with no explanation. Show an inline CTA
  // pointing the user at Settings instead — same affordance the failure
  // mode plan recommended for every "go to Settings" error path.
  if (error) {
    const message = error instanceof Error ? error.message : String(error);
    const isMissingToken = /GitHub token not set/i.test(message);
    if (!isMissingToken) {
      // Other discovery errors (network, 5xx) shouldn't masquerade as a
      // missing-token CTA — render a generic banner and let the user retry.
      return (
        <div className={standalone ? '' : 'mt-12 border-t border-white/10 pt-8'}>
          {!standalone && (
            <h2 className="mb-2 text-lg font-medium text-white/70">Community Games</h2>
          )}
          <p className="text-sm text-white/40">
            Couldn&apos;t load community games right now. {message}
          </p>
        </div>
      );
    }
    return (
      <div className={standalone ? '' : 'mt-12 border-t border-white/10 pt-8'}>
        {!standalone && <h2 className="mb-2 text-lg font-medium text-white/70">Community Games</h2>}
        <div className="rounded-lg border border-white/10 bg-white/5 p-4">
          <p className="text-sm text-white/60">
            Browse other team games once you connect a GitHub token.
          </p>
          <p className="mt-1 text-xs text-white/40">
            You can do this any time — it&apos;s only needed for browsing and publishing.
          </p>
          <Button onClick={() => navigate('/settings')} className="mt-3">
            Open Settings
          </Button>
        </div>
      </div>
    );
  }

  // Disabled queries have no data without loading; hide the section until discovery starts.
  if (!isLoading && !discovered) return null;

  if (isLoading || !discovered)
    return (
      <div
        className={
          standalone
            ? 'min-h-64'
            : 'mt-12 min-h-[calc(100vh-12rem)] border-t border-white/[0.08] pt-8'
        }
      >
        {!standalone && (
          <h2 className="mb-6 f-title" style={{ color: 'var(--text-2)' }}>
            Community Games
          </h2>
        )}
        <div
          className="flex items-center justify-center gap-3 py-12 text-sm text-white/40"
          role="status"
        >
          <div
            className="h-6 w-6 animate-spin rounded-full border-2 border-white/20 border-t-white/60"
            aria-hidden="true"
          />
          <span>Loading community games...</span>
        </div>
      </div>
    );

  // Filter out local games, (optionally) test games, and apply name search
  const communityGames = filterDashboardCommunityGames(discovered, preferences, {
    localGameIds,
    showTestGames,
    searchQuery,
  }).sort((a, b) => a.displayName.localeCompare(b.displayName));
  if (communityGames.length === 0) {
    if (!standalone) return null;
    return (
      <DashboardEmptyView
        title={searchQuery.trim() ? 'No matching community games' : 'No community games yet'}
        detail={
          searchQuery.trim()
            ? `No community games match “${searchQuery}”.`
            : 'Games shared by your team will appear here.'
        }
      />
    );
  }

  return (
    <div className={standalone ? '' : 'mt-12 border-t border-white/[0.08] pt-8'}>
      {!standalone && (
        <h2 className="mb-4 f-title" style={{ color: 'var(--text-2)' }}>
          Community Games
        </h2>
      )}
      {/* The grid renders identically in both the new and legacy UI. */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {communityGames.map((game) => (
          <CommunityGameCard
            key={dashboardPreferenceKey({
              scope: 'community',
              gameId: game.gameId,
              repoFullName: game.repoFullName,
            })}
            game={game}
            favorite={isDashboardFavorited(preferences, {
              scope: 'community',
              gameId: game.gameId,
              repoFullName: game.repoFullName,
            })}
            onArchive={() => onArchive({ ...communityPreferencePayload(game), archived: true })}
            onFavorite={(favorite) => onFavorite({ ...communityPreferencePayload(game), favorite })}
          />
        ))}
      </div>
    </div>
  );
}

export function CommunityGameRow({
  game,
  isLast,
}: {
  game: DiscoveredGame;
  isLast?: boolean;
}): React.JSX.Element {
  const queryClient = useQueryClient();
  const capabilities = useClientCapabilities();
  const canCloneGame = canInvokeChannel(capabilities, 'game:clone');
  const [cloning, setCloning] = useState(false);
  const [cloneError, setCloneError] = useState<string | null>(null);
  const [imgFailed, setImgFailed] = useState(false);

  useEffect(() => {
    setImgFailed(false);
  }, [game.heroImage]);

  const handleClone = async (e: React.MouseEvent): Promise<void> => {
    e.preventDefault();
    e.stopPropagation();
    if (cloning || !canCloneGame) return;
    setCloning(true);
    setCloneError(null);
    try {
      await window.crucible.invoke('game:clone', {
        project: { placement: 'local', gameId: game.gameId },
        repoFullName: game.repoFullName,
      });
      invalidateGameLists(queryClient);
      void queryClient.invalidateQueries({ queryKey: ['game:discover'] });
    } catch (err) {
      setCloneError(err instanceof Error ? err.message : 'Clone failed');
      setTimeout(() => setCloneError(null), 5000);
    } finally {
      setCloning(false);
    }
  };

  return (
    <div className="group relative">
      <div
        className="relative flex items-center gap-3 rounded-xl px-3 py-[14px] transition-colors group-hover:bg-[var(--surface-row-hover)]"
        style={{
          transitionDuration: 'var(--motion-fast)',
          transitionTimingFunction: 'var(--ease-flora)',
        }}
      >
        <div className="relative h-12 w-[84px] shrink-0 overflow-hidden rounded-lg bg-white/[0.04]">
          {game.heroImage && !imgFailed ? (
            <img
              src={game.heroImage}
              alt={game.displayName}
              className="h-full w-full object-cover"
              loading="lazy"
              decoding="async"
              onError={() => setImgFailed(true)}
            />
          ) : (
            <GenreFallback
              genreKey={inferGenreKey(game.displayName, game.description)}
              alt={game.displayName}
              className="h-full w-full object-cover"
            />
          )}
        </div>

        {/* Name */}
        <p className="min-w-0 flex-1 truncate text-sm" style={{ color: 'var(--text-2)' }}>
          {game.displayName}
        </p>

        {/* Updated date */}
        <span className="shrink-0 text-xs tabular-nums" style={{ color: 'var(--text-3)' }}>
          {formatTimeAgo(game.updatedAt)}
        </span>

        {/* Contributor avatars — stacked, rightmost is top contributor */}
        {game.contributors && game.contributors.length > 0 && (
          <div className="flex shrink-0 items-center">
            {game.contributors.slice(0, 5).map((c, i) => (
              <img
                key={c.login}
                src={c.avatarUrl}
                alt={c.login}
                title={c.login}
                className="h-8 w-8 rounded-full ring-2 ring-black"
                style={{ marginLeft: i === 0 ? 0 : '-10px', zIndex: i }}
              />
            ))}
          </div>
        )}

        {/* Clone error */}
        {cloneError && <span className="shrink-0 text-xs text-red-400">{cloneError}</span>}

        {/* Action — outlined + download when remote, filled + harddrive when local */}
        {game.isLocal ? (
          <div
            className="shrink-0 rounded-full p-1.5"
            style={{ background: 'var(--border-alpha-1)', color: 'var(--text-2)' }}
            title="Cloned locally"
          >
            <Check size={16} strokeWidth={2} />
          </div>
        ) : canCloneGame ? (
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClone}
            disabled={cloning}
            title="Clone to edit"
            className="shrink-0"
            aria-label={`Clone ${game.displayName} to edit locally`}
          >
            {cloning ? (
              <div className="h-4 w-4 animate-spin rounded-full border border-white/20 border-t-white/60" />
            ) : (
              <Download size={16} strokeWidth={1.75} />
            )}
          </Button>
        ) : null}
      </div>
      {/* Divider — inset by rounded-xl radius (12px) so it stops at the corner.
          Sits behind the hover surface (z-0) so hover bg covers it. */}
      {!isLast && (
        <div
          className="absolute bottom-0 left-3 right-3 h-px transition-opacity group-hover:opacity-0"
          style={{ background: 'var(--border-alpha-1)', transitionDuration: 'var(--motion-fast)' }}
        />
      )}
    </div>
  );
}

function CommunityGameCard({
  game,
  favorite = false,
  onFavorite,
  onArchive,
}: {
  game: DiscoveredGame;
  favorite?: boolean;
  onFavorite?: (favorite: boolean) => Promise<void>;
  onArchive?: () => Promise<void>;
}): React.JSX.Element {
  const queryClient = useQueryClient();
  const capabilities = useClientCapabilities();
  const canCloneGame = canInvokeChannel(capabilities, 'game:clone');
  const [cloning, setCloning] = useState(false);
  const [cloneError, setCloneError] = useState<string | null>(null);
  const [imgFailed, setImgFailed] = useState(false);

  // Reset failure state when the image URL changes (e.g. after re-fetch)
  useEffect(() => {
    setImgFailed(false);
  }, [game.heroImage]);

  const handleClone = async (e: React.MouseEvent): Promise<void> => {
    e.preventDefault();
    e.stopPropagation();
    if (cloning || !canCloneGame) return;
    setCloning(true);
    setCloneError(null);
    try {
      await window.crucible.invoke('game:clone', {
        project: { placement: 'local', gameId: game.gameId },
        repoFullName: game.repoFullName,
      });
      invalidateGameLists(queryClient);
      void queryClient.invalidateQueries({ queryKey: ['game:discover'] });
    } catch (err) {
      setCloneError(err instanceof Error ? err.message : 'Clone failed');
      setTimeout(() => setCloneError(null), 5000);
    } finally {
      setCloning(false);
    }
  };

  const handleFavorite = (e: React.MouseEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    void onFavorite?.(!favorite);
  };

  const handleArchive = (e: React.MouseEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    if (
      confirm(
        `Archive "${game.displayName}"? It will be hidden from the Dashboard and Community Games until you restore it.`,
      )
    ) {
      void onArchive?.();
    }
  };

  return (
    <div
      className="card-gradient group flex flex-col overflow-hidden rounded-2xl transition-all hover:brightness-[1.08]"
      style={{
        boxShadow: 'var(--hairline), var(--shadow-card)',
        transitionTimingFunction: 'var(--ease-flora)',
        transitionDuration: 'var(--motion-fast)',
      }}
    >
      {/* Hero image / placeholder.
          #603: swap the previous letter-avatar fallback for the same
          <GenreFallback> the dashboard's main GameCard uses, so cloned
          games and community games share a single visual fallback
          system. Genre is inferred from displayName + description
          (the GitHub repo description threaded through the discover
          flow), matching the keyword set the Fal.ai prompt builder
          would have used. */}
      <div className="relative flex aspect-video w-full items-center justify-center bg-gradient-to-br from-crucible-accent/20 to-purple-500/20">
        {game.heroImage && !imgFailed ? (
          <img
            src={game.heroImage}
            alt={game.displayName}
            className="h-full w-full object-cover"
            onError={() => setImgFailed(true)}
          />
        ) : (
          <GenreFallback
            genreKey={inferGenreKey(game.displayName, game.description)}
            alt={game.displayName}
            className="h-full w-full object-cover"
          />
        )}
        {cloning && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/60 backdrop-blur-sm">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-white/20 border-t-crucible-accent" />
            <span className="text-xs font-medium text-white/70">Cloning...</span>
          </div>
        )}
        {cloneError && (
          <div className="absolute inset-x-0 bottom-0 bg-red-900/90 px-3 py-2 text-xs text-red-200 backdrop-blur-sm">
            {cloneError}
          </div>
        )}
        {onFavorite && (
          <Button
            variant="ghost"
            size="icon"
            onClick={handleFavorite}
            className={`absolute left-2 top-2 h-7 w-7 rounded-md bg-black/60 ${favorite ? 'text-[var(--color-brand)]' : 'text-white/55'} opacity-0 backdrop-blur-sm hover:bg-black/80 hover:text-[var(--color-brand)] group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100`}
            title={favorite ? 'Remove from favorites' : 'Add to favorites'}
            aria-label={
              favorite ? `Unfavorite ${game.displayName}` : `Favorite ${game.displayName}`
            }
          >
            <Star size={14} strokeWidth={1.8} fill={favorite ? 'currentColor' : 'none'} />
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            openExternalUrl(game.repoUrl);
          }}
          className="absolute right-2 top-2 h-7 w-7 rounded-md bg-black/60 text-white/55 opacity-0 backdrop-blur-sm hover:bg-black/80 hover:text-white/90 group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100"
          title="Open GitHub repo"
          aria-label={`Open GitHub repo for ${game.displayName}`}
        >
          <ExternalLink size={14} strokeWidth={1.8} />
        </Button>
        {onArchive && (
          <Button
            variant="ghost"
            size="icon"
            onClick={handleArchive}
            className="absolute right-2 top-11 h-7 w-7 rounded-md bg-black/60 text-white/55 opacity-0 backdrop-blur-sm hover:bg-black/80 hover:text-white/90 group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100"
            title="Archive game"
            aria-label={`Archive ${game.displayName}`}
          >
            <Archive size={14} strokeWidth={1.8} />
          </Button>
        )}
      </div>

      {/* Info */}
      <div className="flex flex-1 flex-col gap-1 p-4">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-medium group-hover:text-white" style={{ color: 'var(--text-1)' }}>
            {game.displayName}
          </h3>
          {game.isLocal ? (
            <span className="shrink-0 rounded-full bg-green-500/20 px-2 py-0.5 text-xs font-medium text-green-400">
              Local copy
            </span>
          ) : canCloneGame ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClone}
              disabled={cloning}
              className="shrink-0"
            >
              {cloning ? 'Cloning...' : 'Clone to edit'}
            </Button>
          ) : null}
        </div>
        {game.description && (
          <p className="line-clamp-2 text-xs text-white/40">{game.description}</p>
        )}
        {!game.isLocal && canCloneGame && (
          <p className="text-xs text-white/30">Creates a local editable copy on this Mac.</p>
        )}
        <p className="text-xs text-white/30">{formatTimeAgo(game.updatedAt)}</p>
      </div>
    </div>
  );
}
